/**
 * Financial Health Dashboard
 *
 * Visar företagets ekonomiska hälsa baserat på SIE-data.
 * Trafikljus, nyckeltal, trender, balansstruktur, varningar och utdelningskapacitet.
 */

import { useState, useEffect } from "react";
import {
  HeartPulse,
  TrendingUp,
  TrendingDown,
  Banknote,
  Scale,
  Landmark,
  AlertTriangle,
  CheckCircle,
  Info,
  XCircle,
  Shield,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Percent,
  Wallet,
  Calculator,
  Gauge,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import {
  StatCard,
  ChartCard,
  BarChartWidget,
  DonutChartWidget,
} from "@/components/charts";
import { formatCurrency, formatPercent, formatCompact } from "@/lib/format";
import {
  companiesApi,
  type AnnualReport,
  type AnnualReportSummary,
  type CompanyWithDetails,
  type ParsedSieData,
  type BalanceSheet,
  type IncomeStatement,
  type KeyFigures,
} from "@/lib/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HealthLevel = "good" | "warning" | "danger";

interface HealthDimension {
  id: string;
  label: string;
  value: string;
  level: HealthLevel;
  description: string;
}

interface Insight {
  level: "danger" | "warning" | "info" | "success";
  title: string;
  description: string;
  legalRef?: string;
}

interface FinancialHealthProps {
  report: AnnualReport | null;
  company: CompanyWithDetails;
  isLoading: boolean;
  /** Completed/exported reports available for selection */
  availableReports?: AnnualReportSummary[];
  /** Currently selected report id */
  selectedReportId?: string;
  /** Callback when user picks a different fiscal year */
  onSelectReport?: (reportId: string) => void;
}

// ---------------------------------------------------------------------------
// Analytics Model Suitability per Company Profile
// ---------------------------------------------------------------------------
// Some financial models (like Altman Z-Score) were developed for specific
// company types and give misleading results for others.

type AnalyticsModel =
  | "altmanZScore"
  | "dupontAnalysis"
  | "workingCapitalCycle"
  | "debtAnalysis"
  | "sustainableGrowth"
  | "liquidationValue"
  | "riskIndicators"
  | "mnaReadiness"
  // Profile-specific models
  | "realEstateMetrics"
  | "holdingMetrics"
  | "knowledgeCompanyMetrics";

// Define which profiles each model is NOT suitable for
const MODEL_EXCLUSIONS: Record<AnalyticsModel, CompanyProfile[]> = {
  // Altman Z-Score: Developed for manufacturing, not suitable for:
  // - Property: High assets, low turnover = artificially low score
  // - Holding: Financial assets, no operating revenue
  // - Investment: Same as holding
  altmanZScore: ["property", "holding", "investment"],

  // DuPont: Works for most, but less meaningful for pure holding
  dupontAnalysis: ["holding"],

  // Working Capital Cycle: Not relevant for non-operating companies
  workingCapitalCycle: ["property", "holding", "investment"],

  // Debt Analysis: Relevant for all
  debtAnalysis: [],

  // Sustainable Growth: Works for all operating companies
  sustainableGrowth: ["holding"],

  // Liquidation Value: Relevant for all
  liquidationValue: [],

  // Risk Indicators: Relevant for all
  riskIndicators: [],

  // M&A Readiness: Relevant for all
  mnaReadiness: [],

  // Profile-specific models - only shown for their target profiles
  realEstateMetrics: ["service", "trading", "manufacturing", "knowledge", "holding", "investment", "general"],
  holdingMetrics: ["service", "trading", "manufacturing", "knowledge", "property", "general"],
  knowledgeCompanyMetrics: ["property", "holding", "investment", "trading", "manufacturing", "general"],
};

function isModelSuitableForProfile(model: AnalyticsModel, profile: CompanyProfile): boolean {
  return !MODEL_EXCLUSIONS[model].includes(profile);
}

// ---------------------------------------------------------------------------
// Health computation helpers
// ---------------------------------------------------------------------------

function computeHealthDimensions(
  kf: KeyFigures,
  bs: BalanceSheet,
  is: IncomeStatement
): HealthDimension[] {
  const equity = bs.equityAndLiabilities.equity;
  const totalAssets = bs.assets.totalAssets;
  const currentAssetsTotal = bs.assets.currentAssets.total;
  const shortTermLiabilities = bs.equityAndLiabilities.shortTermLiabilities;
  const longTermLiabilities = bs.equityAndLiabilities.longTermLiabilities;
  const debtRatio =
    totalAssets > 0
      ? ((longTermLiabilities + shortTermLiabilities) / totalAssets) * 100
      : 0;
  const currentRatio =
    shortTermLiabilities > 0
      ? currentAssetsTotal / shortTermLiabilities
      : currentAssetsTotal > 0
        ? 999
        : 0;

  return [
    {
      id: "soliditet",
      label: "Soliditet",
      value: formatPercent(kf.solidityPercent),
      level:
        kf.solidityPercent >= 30
          ? "good"
          : kf.solidityPercent >= 20
            ? "warning"
            : "danger",
      description:
        kf.solidityPercent >= 30
          ? "Stark finansiell grund"
          : kf.solidityPercent >= 20
            ? "Acceptabel men kan stärkas"
            : "Svag soliditet — hög risk",
    },
    {
      id: "lonsamhet",
      label: "Lönsamhet",
      value: formatCurrency(is.netResult),
      level:
        is.netResult > 0 ? "good" : is.netResult === 0 ? "warning" : "danger",
      description:
        is.netResult > 0
          ? "Lönsam verksamhet"
          : is.netResult === 0
            ? "Nollresultat"
            : "Förlust under perioden",
    },
    {
      id: "likviditet",
      label: "Likviditet",
      value:
        currentRatio >= 999
          ? "Inga skulder"
          : currentRatio.toLocaleString("sv-SE", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            }),
      level:
        currentRatio >= 1.5
          ? "good"
          : currentRatio >= 1.0
            ? "warning"
            : "danger",
      description:
        currentRatio >= 1.5
          ? "God betalningsförmåga"
          : currentRatio >= 1.0
            ? "Tillräcklig likviditet"
            : "Risk för betalningssvårigheter",
    },
    {
      id: "skuldsattning",
      label: "Skuldsättning",
      value: formatPercent(debtRatio),
      level:
        debtRatio < 50 ? "good" : debtRatio <= 70 ? "warning" : "danger",
      description:
        debtRatio < 50
          ? "Låg skuldsättning"
          : debtRatio <= 70
            ? "Måttlig skuldsättning"
            : "Hög skuldsättning",
    },
    {
      id: "kapitalskydd",
      label: "Kapitalskydd",
      value:
        equity.total >= equity.shareCapital * 2
          ? "Starkt"
          : equity.total >= equity.shareCapital
            ? "OK"
            : equity.total >= equity.shareCapital / 2
              ? "Svagt"
              : "Kritiskt",
      level:
        equity.total >= equity.shareCapital * 2
          ? "good"
          : equity.total >= equity.shareCapital
            ? "good"
            : equity.total >= equity.shareCapital / 2
              ? "warning"
              : "danger",
      description:
        equity.total < equity.shareCapital / 2
          ? "Kontrollbalansräkning kan krävas (ABL 25:13)"
          : equity.total < equity.shareCapital
            ? "Eget kapital under aktiekapitalet"
            : "Aktiekapitalet är skyddat",
    },
  ];
}

function computeHealthScore(dimensions: HealthDimension[]): number {
  const levelScore: Record<HealthLevel, number> = {
    good: 5,
    warning: 3,
    danger: 1,
  };
  const sum = dimensions.reduce((acc, d) => acc + levelScore[d.level], 0);
  return Math.round((sum / dimensions.length) * 10) / 10;
}

function getScoreLabel(score: number): { label: string; description: string } {
  if (score >= 4.5)
    return {
      label: "Utmärkt hälsa",
      description: "Företaget har en stark finansiell position inom samtliga dimensioner.",
    };
  if (score >= 3.5)
    return {
      label: "God hälsa",
      description: "Företaget har en stabil grund men enstaka områden kan förbättras.",
    };
  if (score >= 2.5)
    return {
      label: "Acceptabel hälsa",
      description: "Flera dimensioner visar varningssignaler som bör uppmärksammas.",
    };
  if (score >= 1.5)
    return {
      label: "Svag hälsa",
      description: "Företaget har betydande utmaningar inom flera finansiella dimensioner.",
    };
  return {
    label: "Kritisk hälsa",
    description: "Företaget befinner sig i en kritisk situation som kräver omedelbara åtgärder.",
  };
}

/** Threshold explanations shown when a dimension is expanded */
const DIMENSION_THRESHOLDS: Record<string, { green: string; yellow: string; red: string }> = {
  soliditet: {
    green: "30 % eller mer — stark finansiell grund",
    yellow: "20–30 % — acceptabelt men kan stärkas",
    red: "Under 20 % — hög skuldsättning relativt tillgångarna",
  },
  lonsamhet: {
    green: "Positivt resultat — lönsam verksamhet",
    yellow: "Nollresultat — balans mellan intäkter och kostnader",
    red: "Negativt resultat — verksamheten gick med förlust",
  },
  likviditet: {
    green: "Balanslikviditet ≥ 1,5 — god betalningsförmåga",
    yellow: "1,0–1,5 — tillräcklig men begränsad likviditet",
    red: "Under 1,0 — risk för betalningssvårigheter",
  },
  skuldsattning: {
    green: "Under 50 % — låg skuldsättning",
    yellow: "50–70 % — måttlig skuldsättning",
    red: "Över 70 % — hög skuldsättning",
  },
  kapitalskydd: {
    green: "Eget kapital överstiger aktiekapitalet — skyddat",
    yellow: "Eget kapital under aktiekapitalet men över halva — varning",
    red: "Under halva aktiekapitalet — kontrollbalansräkning kan krävas (ABL 25:13)",
  },
};

function computeInsights(
  kf: KeyFigures,
  bs: BalanceSheet,
  is: IncomeStatement,
  userInput?: AnnualReport["userInput"]
): Insight[] {
  const equity = bs.equityAndLiabilities.equity;
  const totalAssets = bs.assets.totalAssets;
  const longTermLiabilities = bs.equityAndLiabilities.longTermLiabilities;
  const shortTermLiabilities = bs.equityAndLiabilities.shortTermLiabilities;
  const debtRatio =
    totalAssets > 0
      ? ((longTermLiabilities + shortTermLiabilities) / totalAssets) * 100
      : 0;

  const insights: Insight[] = [];

  // Danger
  if (equity.total < equity.shareCapital / 2) {
    insights.push({
      level: "danger",
      title: "Kontrollbalansräkning kan krävas",
      description: `Eget kapital (${formatCurrency(equity.total)}) understiger halva aktiekapitalet (${formatCurrency(equity.shareCapital / 2)}). Styrelsen ska genast upprätta kontrollbalansräkning.`,
      legalRef: "ABL 25 kap 13§",
    });
  }

  if (equity.total < 0) {
    insights.push({
      level: "danger",
      title: "Negativt eget kapital",
      description: `Eget kapital är ${formatCurrency(equity.total)}. Akut situation som kräver åtgärder.`,
    });
  }

  // Warnings
  if (kf.solidityPercent < 20 && kf.solidityPercent >= 0) {
    insights.push({
      level: "warning",
      title: "Låg soliditet",
      description: `Soliditeten är ${formatPercent(kf.solidityPercent)} vilket indikerar hög skuldsättning. Överväg kapitaltillskott.`,
    });
  }

  if (debtRatio > 70) {
    insights.push({
      level: "warning",
      title: "Hög skuldsättning",
      description: `Skuldsättningsgraden är ${formatPercent(debtRatio)}. Höga skulder ökar risken vid omsättningsnedgång.`,
    });
  }

  if (is.netResult < 0) {
    insights.push({
      level: "warning",
      title: "Verksamheten gick med förlust",
      description: `Årets resultat: ${formatCurrency(is.netResult)}. Analysera kostnaderna och intäktsmöjligheterna.`,
    });
  }

  const proposedDividend = userInput?.proposedDividend;
  if (
    proposedDividend &&
    proposedDividend > 0 &&
    is.netResult > 0 &&
    proposedDividend > is.netResult * 2
  ) {
    insights.push({
      level: "warning",
      title: "Hög utdelning i förhållande till resultat",
      description: `Föreslagen utdelning (${formatCurrency(proposedDividend)}) är mer än dubbla årets resultat (${formatCurrency(is.netResult)}).`,
    });
  }

  // Success
  if (kf.solidityPercent > 40) {
    insights.push({
      level: "success",
      title: "Stark soliditet",
      description: `Soliditeten är ${formatPercent(kf.solidityPercent)} — stabil finansiell position.`,
    });
  }

  if (is.netResult > 0 && !insights.some((i) => i.title.includes("soliditet") && i.level === "success")) {
    insights.push({
      level: "success",
      title: "Positivt resultat",
      description: `Årets resultat: ${formatCurrency(is.netResult)} — lönsam verksamhet.`,
    });
  }

  // Info
  if (bs.equityAndLiabilities.untaxedReserves > 0) {
    insights.push({
      level: "info",
      title: "Obeskattade reserver",
      description: `Bolaget har ${formatCurrency(bs.equityAndLiabilities.untaxedReserves)} i obeskattade reserver (periodiseringsfonder m.m.).`,
    });
  }

  return insights;
}

function trendPercent(
  current: number,
  previous: number | undefined | null
): number | undefined {
  if (previous == null || previous === 0) return undefined;
  return ((current - previous) / Math.abs(previous)) * 100;
}

// ---------------------------------------------------------------------------
// Company profile detection
// ---------------------------------------------------------------------------

type CompanyProfile = "holding" | "investment" | "property" | "knowledge" | "service" | "trading" | "manufacturing" | "general";

interface ProfileInfo {
  id: CompanyProfile;
  label: string;
  description: string;
  primaryMethod: "substans" | "earnings" | "ebitda";
  methodNote: string;
}

interface ProfileFactor {
  label: string;
  value: string;
  meetsThreshold: boolean;
}

interface ProfileDetectionResult {
  profile: ProfileInfo;
  confidence: number; // 0-100
  factors: ProfileFactor[];
  reasoning: string;
  alternativeProfiles: { profile: ProfileInfo; score: number }[];
  warnings: string[];
}

const PROFILES: Record<CompanyProfile, ProfileInfo> = {
  holding: {
    id: "holding",
    label: "Holdingbolag",
    description:
      "Bolaget äger primärt andelar i dotterbolag eller andra finansiella placeringar. Låg eller ingen operativ verksamhet.",
    primaryMethod: "substans",
    methodNote:
      "Substansvärde är primär metod, men bokfört värde på aktier i dotterbolag (anskaffningsvärde under K2) kan avvika kraftigt från verkligt värde. Värdet beror på underliggande bolags värde.",
  },
  investment: {
    id: "investment",
    label: "Investmentbolag",
    description:
      "Bolaget äger primärt noterade värdepapper eller andra finansiella instrument med observerbara marknadsvärden.",
    primaryMethod: "substans",
    methodNote:
      "Substansvärde baserat på marknadsvärdet av innehaven. Till skillnad från holdingbolag har investeringarna ofta observerbara marknadspriser.",
  },
  property: {
    id: "property",
    label: "Fastighetsbolag",
    description:
      "Hög andel byggnader/mark i balansräkningen. Under K2 bokförs fastigheter till anskaffningsvärde minus avskrivningar, vilket ofta kraftigt understiger marknadsvärdet.",
    primaryMethod: "substans",
    methodNote:
      "Substansvärde med marknadsjusterade fastighetsvärden är den primära metoden. Bokförda värden under K2 understiger normalt marknadsvärdet väsentligt.",
  },
  knowledge: {
    id: "knowledge",
    label: "Kunskapsbolag",
    description:
      "Konsult- eller rådgivningsföretag där värdet primärt ligger i medarbetarnas kompetens, kundrelationer och varumärke — tillgångar som inte syns i balansräkningen.",
    primaryMethod: "earnings",
    methodNote:
      "Avkastningsvärde (P/E) är primär metod, men värdet är starkt beroende av nyckelpersoner. Substansvärdet är ofta mycket lågt och missvisande.",
  },
  service: {
    id: "service",
    label: "Tjänsteföretag",
    description:
      "Verksamheten bygger på personal och kompetens snarare än fysiska tillgångar.",
    primaryMethod: "earnings",
    methodNote:
      "Avkastningsvärde (P/E) är den primära metoden. Substansvärdet avspeglar sällan det verkliga värdet i tjänsteföretag.",
  },
  trading: {
    id: "trading",
    label: "Handelsföretag",
    description:
      "Hög andel varulager och varuinköp relativt omsättningen.",
    primaryMethod: "earnings",
    methodNote:
      "Avkastningsvärde (P/E) med hänsyn till varulagrets storlek. Substansvärdet kan vara relevant som golvvärde.",
  },
  manufacturing: {
    id: "manufacturing",
    label: "Tillverkningsföretag",
    description:
      "Kapitalintensivt med hög andel maskiner/inventarier och varulager.",
    primaryMethod: "ebitda",
    methodNote:
      "EV/EBITDA ger ofta en rättvisande bild då avskrivningar på produktionsutrustning kan vara betydande.",
  },
  general: {
    id: "general",
    label: "Blandat bolag",
    description:
      "Bolaget matchar inget enskilt profilmönster. Alla tre metoderna vägs samman.",
    primaryMethod: "substans",
    methodNote:
      "Samtliga metoder vägs samman för en balanserad indikation.",
  },
};

function detectCompanyProfile({
  buildings,
  tangibleAssets,
  financialAssets,
  totalAssets,
  inventory,
  netSales,
  personnelCosts,
  goodsAndMaterials,
  previousBuildings,
}: {
  buildings: number | undefined;
  tangibleAssets: number;
  financialAssets: number;
  totalAssets: number;
  inventory: number;
  netSales: number;
  personnelCosts: number;
  goodsAndMaterials: number;
  previousBuildings?: number;
}): ProfileDetectionResult {
  const warnings: string[] = [];

  if (totalAssets <= 0) {
    return {
      profile: PROFILES.general,
      confidence: 0,
      factors: [],
      reasoning: "Inga tillgångar att analysera.",
      alternativeProfiles: [],
      warnings: [],
    };
  }

  // Calculate all metrics
  const buildingValue = buildings ?? 0;
  const buildingShare = buildingValue / totalAssets;
  const tangibleShare = tangibleAssets / totalAssets;
  const financialShare = financialAssets / totalAssets;
  const inventoryShare = inventory / totalAssets;
  const absNetSales = Math.abs(netSales);
  const personnelOfRevenue = absNetSales > 0 ? Math.abs(personnelCosts) / absNetSales : 0;
  const goodsOfRevenue = absNetSales > 0 ? Math.abs(goodsAndMaterials) / absNetSales : 0;
  const revenueToAssets = absNetSales / totalAssets;

  // Check for recent building acquisition (point 5)
  if (previousBuildings !== undefined && buildingValue > 0) {
    const buildingIncrease = buildingValue - previousBuildings;
    const increasePercent = previousBuildings > 0
      ? (buildingIncrease / previousBuildings) * 100
      : (buildingIncrease > 0 ? 100 : 0);

    if (increasePercent > 30) {
      warnings.push(
        `Byggnader har ökat med ${Math.round(increasePercent)}% under året. ` +
        `Vid nyförvärv ligger bokfört värde nära marknadsvärdet — marknadsjusteringen kan vara överdriven.`
      );
    }
  }

  // Calculate scores for each profile (0-100)
  // New logic: Prioritize business model (cost structure) over asset structure
  const scores: Record<CompanyProfile, { score: number; factors: ProfileFactor[] }> = {
    holding: { score: 0, factors: [] },
    investment: { score: 0, factors: [] },
    property: { score: 0, factors: [] },
    knowledge: { score: 0, factors: [] },
    service: { score: 0, factors: [] },
    trading: { score: 0, factors: [] },
    manufacturing: { score: 0, factors: [] },
    general: { score: 20, factors: [] }, // Base score for fallback
  };

  // === HOLDING COMPANY DETECTION (check first - passive companies) ===
  // High financial assets + very low revenue relative to assets + low personnel/goods costs
  const holdingFactors: ProfileFactor[] = [
    {
      label: "Finansiella tillgångar/totalt",
      value: `${(financialShare * 100).toFixed(0)}%`,
      meetsThreshold: financialShare > 0.4,
    },
    {
      label: "Omsättning/tillgångar",
      value: `${(revenueToAssets * 100).toFixed(0)}%`,
      meetsThreshold: revenueToAssets < 0.15,
    },
    {
      label: "Materiella tillgångar/totalt",
      value: `${(tangibleShare * 100).toFixed(0)}%`,
      meetsThreshold: tangibleShare < 0.2,
    },
  ];
  scores.holding.factors = holdingFactors;

  // Strong signal: financial assets dominate
  if (financialShare > 0.5) scores.holding.score += 45;
  else if (financialShare > 0.4) scores.holding.score += 30;
  else if (financialShare > 0.3) scores.holding.score += 15;

  // Very low revenue relative to assets = passive holding
  if (revenueToAssets < 0.05) scores.holding.score += 30;
  else if (revenueToAssets < 0.15) scores.holding.score += 20;
  else if (revenueToAssets < 0.25) scores.holding.score += 10;

  // Low tangible assets (not an operating company)
  if (tangibleShare < 0.1) scores.holding.score += 15;
  else if (tangibleShare < 0.2) scores.holding.score += 10;

  // Penalty if significant operational activity
  if (personnelOfRevenue > 0.3 && absNetSales > totalAssets * 0.2) {
    scores.holding.score -= 25; // Has significant personnel costs = operational
  }
  if (goodsOfRevenue > 0.3) scores.holding.score -= 20; // Trading activity
  if (inventoryShare > 0.1) scores.holding.score -= 15; // Has inventory

  // Add warning for holding companies about subsidiary valuation
  if (scores.holding.score >= 50 && financialShare > 0.3) {
    warnings.push(
      `Finansiella tillgångar (andelar i dotterbolag/intressebolag) bokförs till anskaffningsvärde under K2. ` +
      `Det verkliga värdet kan vara betydligt högre eller lägre beroende på dotterbolagens utveckling.`
    );
  }

  // === INVESTMENT COMPANY DETECTION ===
  // Similar to holding but with focus on traded securities (higher revenue/assets ratio from dividends)
  const investmentFactors: ProfileFactor[] = [
    {
      label: "Finansiella tillgångar/totalt",
      value: `${(financialShare * 100).toFixed(0)}%`,
      meetsThreshold: financialShare > 0.5,
    },
    {
      label: "Omsättning/tillgångar",
      value: `${(revenueToAssets * 100).toFixed(0)}%`,
      meetsThreshold: revenueToAssets > 0.02 && revenueToAssets < 0.3,
    },
    {
      label: "Personalkostnader/omsättning",
      value: `${(personnelOfRevenue * 100).toFixed(0)}%`,
      meetsThreshold: personnelOfRevenue < 0.2,
    },
  ];
  scores.investment.factors = investmentFactors;

  // Investment companies: high financial assets but with some dividend/interest income
  if (financialShare > 0.6) scores.investment.score += 40;
  else if (financialShare > 0.5) scores.investment.score += 30;
  else if (financialShare > 0.4) scores.investment.score += 15;

  // Some revenue from dividends/interest (higher than pure holding)
  if (revenueToAssets > 0.02 && revenueToAssets < 0.3) scores.investment.score += 25;
  if (revenueToAssets > 0.05 && revenueToAssets < 0.2) scores.investment.score += 15;

  // Very low personnel costs (not an operating company)
  if (personnelOfRevenue < 0.1) scores.investment.score += 15;
  else if (personnelOfRevenue < 0.2) scores.investment.score += 10;

  // Penalty for operational activity
  if (goodsOfRevenue > 0.2) scores.investment.score -= 25;
  if (inventoryShare > 0.05) scores.investment.score -= 20;
  if (tangibleShare > 0.2) scores.investment.score -= 15;

  // === KNOWLEDGE/CONSULTING COMPANY DETECTION ===
  // Very high personnel costs + very low tangible assets + high margin potential
  const knowledgeFactors: ProfileFactor[] = [
    {
      label: "Personalkostnader/omsättning",
      value: `${(personnelOfRevenue * 100).toFixed(0)}%`,
      meetsThreshold: personnelOfRevenue > 0.4,
    },
    {
      label: "Materiella tillgångar/totalt",
      value: `${(tangibleShare * 100).toFixed(0)}%`,
      meetsThreshold: tangibleShare < 0.1,
    },
    {
      label: "Varuinköp/omsättning",
      value: `${(goodsOfRevenue * 100).toFixed(0)}%`,
      meetsThreshold: goodsOfRevenue < 0.1,
    },
  ];
  scores.knowledge.factors = knowledgeFactors;

  // Very high personnel costs = knowledge-intensive
  if (personnelOfRevenue > 0.5) scores.knowledge.score += 45;
  else if (personnelOfRevenue > 0.4) scores.knowledge.score += 35;
  else if (personnelOfRevenue > 0.35) scores.knowledge.score += 20;

  // Minimal tangible assets
  if (tangibleShare < 0.05) scores.knowledge.score += 30;
  else if (tangibleShare < 0.1) scores.knowledge.score += 20;
  else if (tangibleShare < 0.15) scores.knowledge.score += 10;

  // No significant goods/materials (pure knowledge work)
  if (goodsOfRevenue < 0.05) scores.knowledge.score += 20;
  else if (goodsOfRevenue < 0.1) scores.knowledge.score += 10;

  // No inventory (not trading/manufacturing)
  if (inventoryShare < 0.02) scores.knowledge.score += 10;

  // Penalty if buildings exist (likely not pure consulting)
  if (buildingShare > 0.1) scores.knowledge.score -= 25;

  // Add warning about key person dependency
  if (scores.knowledge.score >= 50) {
    warnings.push(
      `Kunskapsföretag har ofta ett betydande nyckelpersonsberoende. ` +
      `Värdet kan påverkas kraftigt om nyckelmedarbetare lämnar bolaget.`
    );
  }

  // === SERVICE COMPANY DETECTION (check first - business model priority) ===
  // High personnel costs + low tangible assets + low goods costs = service
  const serviceFactors: ProfileFactor[] = [
    {
      label: "Personalkostnader/omsättning",
      value: `${(personnelOfRevenue * 100).toFixed(0)}%`,
      meetsThreshold: personnelOfRevenue > 0.3,
    },
    {
      label: "Materiella tillgångar/totalt",
      value: `${(tangibleShare * 100).toFixed(0)}%`,
      meetsThreshold: tangibleShare < 0.15,
    },
    {
      label: "Varuinköp/omsättning",
      value: `${(goodsOfRevenue * 100).toFixed(0)}%`,
      meetsThreshold: goodsOfRevenue < 0.2,
    },
  ];
  scores.service.factors = serviceFactors;

  if (personnelOfRevenue > 0.3) scores.service.score += 40;
  if (personnelOfRevenue > 0.5) scores.service.score += 15;
  if (tangibleShare < 0.15) scores.service.score += 25;
  if (tangibleShare < 0.08) scores.service.score += 10;
  if (goodsOfRevenue < 0.2) scores.service.score += 10;
  // Penalty if significant buildings exist (likely not pure service)
  if (buildingShare > 0.15) scores.service.score -= 20;

  // === TRADING COMPANY DETECTION ===
  // High inventory + high goods costs = trading
  const tradingFactors: ProfileFactor[] = [
    {
      label: "Varulager/tillgångar",
      value: `${(inventoryShare * 100).toFixed(0)}%`,
      meetsThreshold: inventoryShare > 0.2,
    },
    {
      label: "Varuinköp/omsättning",
      value: `${(goodsOfRevenue * 100).toFixed(0)}%`,
      meetsThreshold: goodsOfRevenue > 0.4,
    },
    {
      label: "Personalkostnader/omsättning",
      value: `${(personnelOfRevenue * 100).toFixed(0)}%`,
      meetsThreshold: personnelOfRevenue < 0.3,
    },
  ];
  scores.trading.factors = tradingFactors;

  if (inventoryShare > 0.2) scores.trading.score += 35;
  if (inventoryShare > 0.35) scores.trading.score += 15;
  if (goodsOfRevenue > 0.4) scores.trading.score += 35;
  if (goodsOfRevenue > 0.6) scores.trading.score += 15;
  // Penalty for high personnel costs (more likely service/manufacturing hybrid)
  if (personnelOfRevenue > 0.3) scores.trading.score -= 15;

  // === MANUFACTURING COMPANY DETECTION ===
  // High tangible (machinery) + inventory + moderate goods costs
  const manufacturingFactors: ProfileFactor[] = [
    {
      label: "Materiella tillgångar/totalt",
      value: `${(tangibleShare * 100).toFixed(0)}%`,
      meetsThreshold: tangibleShare > 0.3,
    },
    {
      label: "Varulager/tillgångar",
      value: `${(inventoryShare * 100).toFixed(0)}%`,
      meetsThreshold: inventoryShare > 0.1,
    },
    {
      label: "Varuinköp/omsättning",
      value: `${(goodsOfRevenue * 100).toFixed(0)}%`,
      meetsThreshold: goodsOfRevenue > 0.3 && goodsOfRevenue < 0.7,
    },
  ];
  scores.manufacturing.factors = manufacturingFactors;

  if (tangibleShare > 0.3 && buildingShare < 0.25) scores.manufacturing.score += 30;
  if (inventoryShare > 0.1) scores.manufacturing.score += 25;
  if (goodsOfRevenue > 0.3 && goodsOfRevenue < 0.7) scores.manufacturing.score += 25;
  if (personnelOfRevenue > 0.15 && personnelOfRevenue < 0.4) scores.manufacturing.score += 10;
  // Penalty if mostly buildings (more likely property)
  if (buildingShare > 0.25) scores.manufacturing.score -= 25;

  // === PROPERTY COMPANY DETECTION (check last for asset-heavy profiles) ===
  // High buildings OR (tangible dominant + low revenue-to-assets ratio)
  const propertyFactors: ProfileFactor[] = [
    {
      label: "Byggnader/tillgångar",
      value: `${(buildingShare * 100).toFixed(0)}%`,
      meetsThreshold: buildingShare > 0.3,
    },
    {
      label: "Materiella tillgångar/totalt",
      value: `${(tangibleShare * 100).toFixed(0)}%`,
      meetsThreshold: tangibleShare > 0.5,
    },
    {
      label: "Omsättning/tillgångar",
      value: `${(revenueToAssets * 100).toFixed(0)}%`,
      meetsThreshold: revenueToAssets < 0.5,
    },
  ];
  scores.property.factors = propertyFactors;

  if (buildingShare > 0.3) scores.property.score += 50;
  if (buildingShare > 0.5) scores.property.score += 25;
  if (tangibleShare > 0.5 && revenueToAssets < 0.5) scores.property.score += 30;
  if (revenueToAssets < 0.3) scores.property.score += 10;
  // Penalty if high personnel or goods costs (indicates active business, not pure property holding)
  if (personnelOfRevenue > 0.25) scores.property.score -= 15;
  if (goodsOfRevenue > 0.3) scores.property.score -= 15;

  // Find the best profile
  const sortedProfiles = (Object.entries(scores) as [CompanyProfile, { score: number; factors: ProfileFactor[] }][])
    .filter(([id]) => id !== "general")
    .sort((a, b) => b[1].score - a[1].score);

  const [bestProfileId, bestData] = sortedProfiles[0];
  const bestProfile = PROFILES[bestProfileId];
  const bestScore = bestData.score;

  // If best score is too low, fall back to general
  if (bestScore < 40) {
    const generalFactors: ProfileFactor[] = [
      { label: "Byggnader", value: `${(buildingShare * 100).toFixed(0)}%`, meetsThreshold: false },
      { label: "Varulager", value: `${(inventoryShare * 100).toFixed(0)}%`, meetsThreshold: false },
      { label: "Personal/oms.", value: `${(personnelOfRevenue * 100).toFixed(0)}%`, meetsThreshold: false },
      { label: "Varuinköp/oms.", value: `${(goodsOfRevenue * 100).toFixed(0)}%`, meetsThreshold: false },
    ];

    return {
      profile: PROFILES.general,
      confidence: 100 - bestScore, // More confident it's general when other scores are low
      factors: generalFactors,
      reasoning: `Bolaget matchar inget tydligt profilmönster. Högsta matchning var ${bestProfile.label} (${bestScore}%).`,
      alternativeProfiles: sortedProfiles.slice(0, 3).map(([id, data]) => ({
        profile: PROFILES[id],
        score: data.score,
      })),
      warnings,
    };
  }

  // Calculate confidence based on margin to second-best
  const secondBestScore = sortedProfiles[1]?.[1].score ?? 0;
  const margin = bestScore - secondBestScore;
  const confidence = Math.min(95, Math.max(40, bestScore + margin / 2));

  // Build reasoning
  let reasoning = "";

  switch (bestProfileId) {
    case "holding":
      reasoning = `Finansiella tillgångar utgör ${(financialShare * 100).toFixed(0)}% av tillgångarna och omsättning/tillgångar är endast ${(revenueToAssets * 100).toFixed(0)}% — indikerar passivt holdingbolag.`;
      break;
    case "investment":
      reasoning = `Finansiella tillgångar utgör ${(financialShare * 100).toFixed(0)}% av tillgångarna med intäkter från utdelningar/räntor (${(revenueToAssets * 100).toFixed(0)}% av tillgångar).`;
      break;
    case "property":
      reasoning = buildingShare > 0.3
        ? `Byggnader utgör ${(buildingShare * 100).toFixed(0)}% av tillgångarna.`
        : `Hög andel materiella tillgångar (${(tangibleShare * 100).toFixed(0)}%) och låg omsättning relativt tillgångar.`;
      break;
    case "knowledge":
      reasoning = `Personalkostnader utgör ${(personnelOfRevenue * 100).toFixed(0)}% av omsättningen, minimal varuhantering (${(goodsOfRevenue * 100).toFixed(0)}%) och mycket låg andel materiella tillgångar (${(tangibleShare * 100).toFixed(0)}%) — typiskt kunskapsföretag.`;
      break;
    case "service":
      reasoning = `Personalkostnader utgör ${(personnelOfRevenue * 100).toFixed(0)}% av omsättningen och låg andel materiella tillgångar (${(tangibleShare * 100).toFixed(0)}%).`;
      break;
    case "trading":
      reasoning = `Varulager utgör ${(inventoryShare * 100).toFixed(0)}% av tillgångarna och varuinköp ${(goodsOfRevenue * 100).toFixed(0)}% av omsättningen.`;
      break;
    case "manufacturing":
      reasoning = `Materiella tillgångar ${(tangibleShare * 100).toFixed(0)}%, varulager ${(inventoryShare * 100).toFixed(0)}% och varuinköp ${(goodsOfRevenue * 100).toFixed(0)}% av omsättningen.`;
      break;
  }

  return {
    profile: bestProfile,
    confidence: Math.round(confidence),
    factors: bestData.factors,
    reasoning,
    alternativeProfiles: sortedProfiles.slice(1, 3).map(([id, data]) => ({
      profile: PROFILES[id],
      score: data.score,
    })),
    warnings,
  };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const LEVEL_COLORS: Record<HealthLevel, { bg: string; ring: string; text: string }> = {
  good: { bg: "bg-emerald-500", ring: "ring-emerald-200", text: "text-emerald-700" },
  warning: { bg: "bg-amber-500", ring: "ring-amber-200", text: "text-amber-700" },
  danger: { bg: "bg-red-500", ring: "ring-red-200", text: "text-red-700" },
};

function HealthOverview({
  dimensions,
  score,
  fiscalYear,
  availableReports,
  selectedReportId,
  onSelectReport,
}: {
  dimensions: HealthDimension[];
  score: number;
  fiscalYear: string;
  availableReports?: AnnualReportSummary[];
  selectedReportId?: string;
  onSelectReport?: (reportId: string) => void;
}) {
  const [expandedDim, setExpandedDim] = useState<string | null>(null);
  const scoreLevel: HealthLevel =
    score >= 4 ? "good" : score >= 2.5 ? "warning" : "danger";
  const scoreColors = LEVEL_COLORS[scoreLevel];
  const scoreInfo = getScoreLabel(score);

  // Show dropdown if there are multiple reports (even if not all are completed)
  const hasMultipleReports =
    availableReports && availableReports.length > 1 && onSelectReport;

  // Check if a report is completed/exported (can be analyzed)
  const isReportCompleted = (r: AnnualReportSummary) =>
    r.status === "completed" || r.status === "exported";

  // Handle report selection - only allow completed reports
  const handleReportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const reportId = e.target.value;
    const report = availableReports?.find((r) => r.id === reportId);
    if (report && isReportCompleted(report) && onSelectReport) {
      onSelectReport(reportId);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex items-center justify-center shadow-lg shadow-rose-500/25">
            <HeartPulse className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Ekonomisk hälsa
            </h3>
            <p className="text-xs text-gray-500">
              Räkenskapsår {fiscalYear}
            </p>
          </div>
        </div>

        {/* Fiscal year selector - shows all reports, disables non-completed */}
        {hasMultipleReports && (
          <select
            value={selectedReportId || ""}
            onChange={handleReportChange}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {availableReports!.map((r) => {
              const completed = isReportCompleted(r);
              const statusLabel = !completed
                ? r.status === "draft"
                  ? " (utkast)"
                  : " (pågående)"
                : "";
              return (
                <option
                  key={r.id}
                  value={r.id}
                  disabled={!completed}
                  className={!completed ? "text-gray-400" : ""}
                >
                  {r.fiscalYearStart.slice(0, 4)}/{r.fiscalYearEnd.slice(0, 4)}
                  {statusLabel}
                </option>
              );
            })}
          </select>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Health score — same layout as dimension boxes */}
        <div className="flex flex-col">
          <button
            onClick={() => setExpandedDim(expandedDim === "_score" ? null : "_score")}
            className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer w-full"
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center ring-2 ${scoreColors.ring} mb-2`}
            >
              <span className={`text-sm font-bold ${scoreColors.text}`}>
                {score.toLocaleString("sv-SE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
              </span>
            </div>
            <span className="text-xs font-medium text-gray-500 mb-1">
              Hälsopoäng
            </span>
            <span className={`text-sm font-semibold ${scoreColors.text}`}>
              {scoreInfo.label}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 leading-tight">
              av 5.0
            </span>
            <HelpCircle size={12} className="text-gray-300 mt-1.5" />
          </button>
          {expandedDim === "_score" && (
            <div className="mt-1 p-2.5 bg-gray-50 rounded-lg border border-gray-100 text-left">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Poängskala
              </p>
              <p className="text-[11px] text-gray-600 leading-snug">
                {scoreInfo.description}
              </p>
              <div className="mt-2 space-y-0.5 text-[10px] text-gray-500">
                <div>≥4.5 = Utmärkt</div>
                <div>3.5–4.4 = God</div>
                <div>2.5–3.4 = Acceptabel</div>
                <div>1.5–2.4 = Svag</div>
                <div>&lt;1.5 = Kritisk</div>
              </div>
            </div>
          )}
        </div>

        {/* Dimension indicators */}
        {dimensions.map((dim) => {
          const colors = LEVEL_COLORS[dim.level];
          const isExpanded = expandedDim === dim.id;
          const thresholds = DIMENSION_THRESHOLDS[dim.id];
          return (
            <div key={dim.id} className="flex flex-col">
              <button
                onClick={() => setExpandedDim(isExpanded ? null : dim.id)}
                className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer w-full"
              >
                <div
                  className={`w-3 h-3 rounded-full ${colors.bg} ring-2 ${colors.ring} mb-2`}
                />
                <span className="text-xs font-medium text-gray-500 mb-1">
                  {dim.label}
                </span>
                <span className={`text-sm font-semibold ${colors.text}`}>
                  {dim.value}
                </span>
                <span className="text-[10px] text-gray-400 mt-1 leading-tight">
                  {dim.description}
                </span>
                <HelpCircle
                  size={12}
                  className="text-gray-300 mt-1.5"
                />
              </button>
              {/* Expanded threshold explanation */}
              {isExpanded && thresholds && (
                <div className="mt-1 p-2.5 bg-gray-50 rounded-lg border border-gray-100 text-left">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Tröskelvärden
                  </p>
                  <div className="space-y-1">
                    <div className="flex items-start gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-[11px] text-gray-600 leading-snug">
                        {thresholds.green}
                      </span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-[11px] text-gray-600 leading-snug">
                        {thresholds.yellow}
                      </span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-[11px] text-gray-600 leading-snug">
                        {thresholds.red}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const INSIGHT_CONFIG: Record<
  Insight["level"],
  { icon: typeof AlertTriangle; borderColor: string; bgColor: string; iconColor: string; titleColor: string }
> = {
  danger: {
    icon: XCircle,
    borderColor: "border-red-400",
    bgColor: "bg-red-50",
    iconColor: "text-red-500",
    titleColor: "text-red-800",
  },
  warning: {
    icon: AlertTriangle,
    borderColor: "border-amber-400",
    bgColor: "bg-amber-50",
    iconColor: "text-amber-500",
    titleColor: "text-amber-800",
  },
  info: {
    icon: Info,
    borderColor: "border-blue-400",
    bgColor: "bg-blue-50",
    iconColor: "text-blue-500",
    titleColor: "text-blue-800",
  },
  success: {
    icon: CheckCircle,
    borderColor: "border-emerald-400",
    bgColor: "bg-emerald-50",
    iconColor: "text-emerald-500",
    titleColor: "text-emerald-800",
  },
};

function InsightsSection({ insights }: { insights: Insight[] }) {
  if (!insights.length) return null;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <Shield size={18} className="text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">
          Varningar & insikter
        </h3>
      </div>
      <div className="space-y-3">
        {insights.map((insight, i) => {
          const cfg = INSIGHT_CONFIG[insight.level];
          const Icon = cfg.icon;
          return (
            <div
              key={i}
              className={`flex items-start gap-3 border-l-4 ${cfg.borderColor} ${cfg.bgColor} rounded-r-lg px-4 py-3`}
            >
              <Icon size={18} className={`${cfg.iconColor} mt-0.5 flex-shrink-0`} />
              <div className="min-w-0">
                <p className={`text-sm font-medium ${cfg.titleColor}`}>
                  {insight.title}
                </p>
                <p className="text-sm text-gray-600 mt-0.5">
                  {insight.description}
                </p>
                {insight.legalRef && (
                  <p className="text-xs text-gray-400 mt-1">
                    {insight.legalRef}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DividendCapacity({
  equity,
  userInput,
}: {
  equity: BalanceSheet["equityAndLiabilities"]["equity"];
  userInput?: AnnualReport["userInput"];
}) {
  const freeEquity =
    equity.retainedEarnings + equity.currentYearResult;
  const proposedDividend = userInput?.proposedDividend || 0;
  const remaining = freeEquity - proposedDividend;
  const pct = freeEquity > 0 ? (proposedDividend / freeEquity) * 100 : 0;

  if (freeEquity <= 0 && proposedDividend <= 0) return null;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <Banknote size={18} className="text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">
          Utdelningskapacitet
        </h3>
      </div>

      <div className="space-y-4">
        {/* Bar */}
        <div>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="text-gray-600">Fritt eget kapital</span>
            <span className="font-semibold text-gray-900" title={formatCurrency(freeEquity)}>
              {formatCompact(freeEquity)}
            </span>
          </div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
            {proposedDividend > 0 && (
              <div
                className="h-full bg-primary-500 rounded-full transition-all"
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            )}
          </div>
          {proposedDividend > 0 && (
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span title={formatCurrency(proposedDividend)}>
                Föreslagen utdelning: {formatCompact(proposedDividend)} (
                {formatPercent(pct, 0)})
              </span>
              <span title={formatCurrency(remaining)}>Kvarstår: {formatCompact(remaining)}</span>
            </div>
          )}
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-100">
          <div className="text-center">
            <p className="text-xs text-gray-500">Aktiekapital</p>
            <p className="text-sm font-semibold text-gray-900" title={formatCurrency(equity.shareCapital)}>
              {formatCompact(equity.shareCapital)}
            </p>
            <p className="text-[10px] text-gray-400">Bundet</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Balanserat resultat</p>
            <p className="text-sm font-semibold text-gray-900" title={formatCurrency(equity.retainedEarnings)}>
              {formatCompact(equity.retainedEarnings)}
            </p>
            <p className="text-[10px] text-gray-400">Fritt</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Årets resultat</p>
            <p className="text-sm font-semibold text-gray-900" title={formatCurrency(equity.currentYearResult)}>
              {formatCompact(equity.currentYearResult)}
            </p>
            <p className="text-[10px] text-gray-400">Fritt</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfitabilityMargins({
  operatingResult,
  netResult,
  netSales,
}: {
  operatingResult: number;
  netResult: number;
  netSales: number;
}) {
  if (netSales === 0) return null;

  const operatingMargin = (operatingResult / Math.abs(netSales)) * 100;
  const netMargin = (netResult / Math.abs(netSales)) * 100;

  const marginLevel = (m: number): HealthLevel =>
    m >= 10 ? "good" : m >= 3 ? "warning" : "danger";
  const opColors = LEVEL_COLORS[marginLevel(operatingMargin)];
  const netColors = LEVEL_COLORS[marginLevel(netMargin)];

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <Percent size={18} className="text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">
          Lönsamhetsmarginaler
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50 rounded-xl text-center">
          <p className="text-xs text-gray-500 mb-1">Rörelsemarginal</p>
          <p className={`text-2xl font-bold ${opColors.text}`}>
            {operatingMargin.toLocaleString("sv-SE", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
            %
          </p>
          <p className="text-[11px] text-gray-400 mt-1">
            Rörelseresultat / Nettoomsättning
          </p>
        </div>
        <div className="p-4 bg-gray-50 rounded-xl text-center">
          <p className="text-xs text-gray-500 mb-1">Nettomarginal</p>
          <p className={`text-2xl font-bold ${netColors.text}`}>
            {netMargin.toLocaleString("sv-SE", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
            %
          </p>
          <p className="text-[11px] text-gray-400 mt-1">
            Årets resultat / Nettoomsättning
          </p>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-3">
        Rörelsemarginalen visar hur stor andel av omsättningen som blir kvar
        efter rörelsekostnader. Nettomarginalen visar vad som blir kvar
        efter samtliga kostnader inkl. finansnetto och skatt.
      </p>
    </div>
  );
}

function CashPositionIndicator({
  currentCash,
  previousCash,
}: {
  currentCash: number;
  previousCash: number | undefined;
}) {
  const change =
    previousCash != null ? currentCash - previousCash : undefined;
  const changePct =
    previousCash != null && previousCash !== 0
      ? ((currentCash - previousCash) / Math.abs(previousCash)) * 100
      : undefined;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <Wallet size={18} className="text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">
          Kassaposition
        </h3>
      </div>
      <div className="flex items-end gap-4">
        <div>
          <p className="text-xs text-gray-500 mb-1">Kassa & bank</p>
          <p className="text-2xl font-bold text-gray-900" title={formatCurrency(currentCash)}>
            {formatCompact(currentCash)}
          </p>
        </div>
        {change != null && (
          <div className="pb-1">
            <div
              className={`inline-flex items-center gap-1 text-sm font-medium ${
                change >= 0 ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {change >= 0 ? (
                <TrendingUp size={14} />
              ) : (
                <TrendingDown size={14} />
              )}
              {change >= 0 ? "+" : ""}
              {formatCompact(change)}
              {changePct != null && (
                <span className="text-xs text-gray-400 ml-1">
                  ({changePct >= 0 ? "+" : ""}
                  {changePct.toLocaleString("sv-SE", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                  %)
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-400">
              vs föregående räkenskapsår
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface ValuationWarning {
  type: "danger" | "warning" | "info";
  title: string;
  description: string;
  impact: string;
}

function ValuationEstimate({
  adjustedEquity,
  equity,
  shareCapital,
  netResult,
  prevNetResult,
  prevPrevNetResult,
  operatingResult,
  depreciationAmortization,
  totalAssets,
  buildings,
  netSales,
  prevNetSales,
  activeProfile,
  profileDetection,
  isProfileOverridden,
  onProfileChange,
  isSavingProfile,
}: {
  adjustedEquity: number;
  equity: number;
  shareCapital: number;
  netResult: number;
  prevNetResult: number | undefined;
  prevPrevNetResult: number | undefined;
  operatingResult: number;
  depreciationAmortization: number;
  totalAssets: number;
  buildings: number | undefined;
  netSales: number;
  prevNetSales: number | undefined;
  activeProfile: ProfileInfo;
  profileDetection: ProfileDetectionResult;
  isProfileOverridden: boolean;
  onProfileChange: (profile: CompanyProfile | null) => void;
  isSavingProfile: boolean;
}) {

  // --- Valuation warnings ---
  const valuationWarnings: ValuationWarning[] = [];

  // 1. Loss-making / startup company detection
  const isLossMaking = netResult < 0;
  const isConsistentlyLossMaking = netResult < 0 && prevNetResult !== undefined && prevNetResult < 0;
  const hasThreeYearLoss = isConsistentlyLossMaking && prevPrevNetResult !== undefined && prevPrevNetResult < 0;

  if (hasThreeYearLoss) {
    valuationWarnings.push({
      type: "danger",
      title: "Förlust tre år i rad",
      description: "Bolaget har redovisat förlust de senaste tre räkenskapsåren.",
      impact: "Avkastningsvärdering (P/E) är inte tillämplig. Överväg likvidationsvärde eller DCF-analys med framtida prognoser.",
    });
  } else if (isConsistentlyLossMaking) {
    valuationWarnings.push({
      type: "warning",
      title: "Förlust två år i rad",
      description: "Bolaget har redovisat förlust både innevarande och föregående år.",
      impact: "Avkastningsvärdering (P/E) är inte meningsfull med negativt resultat.",
    });
  } else if (isLossMaking) {
    valuationWarnings.push({
      type: "warning",
      title: "Negativt resultat",
      description: `Årets resultat är ${formatCurrency(netResult)}.`,
      impact: "Avkastningsvärdering (P/E) kan inte tillämpas på negativt resultat.",
    });
  }

  // 2. Negative equity warning
  if (equity < 0) {
    valuationWarnings.push({
      type: "danger",
      title: "Negativt eget kapital",
      description: `Eget kapital är ${formatCurrency(equity)}.`,
      impact: "Substansvärdet är negativt och missvisande. Likvidationsvärde bör övervägas.",
    });
  } else if (equity < shareCapital / 2) {
    valuationWarnings.push({
      type: "danger",
      title: "Kapitalbrist — kontrollbalansräkning",
      description: `Eget kapital (${formatCurrency(equity)}) understiger halva aktiekapitalet.`,
      impact: "Bolaget kan vara i kris. Värdering bör baseras på åtgärdsplan eller likvidationsscenario.",
    });
  } else if (adjustedEquity < 0) {
    valuationWarnings.push({
      type: "warning",
      title: "Negativt justerat eget kapital",
      description: "Det justerade egna kapitalet (inkl. latent skatt i obeskattade reserver) är negativt.",
      impact: "Substansvärdet indikerar att skulderna överstiger tillgångarna.",
    });
  }

  // 3. High growth detection
  const revenueGrowth = prevNetSales && prevNetSales > 0
    ? ((Math.abs(netSales) - Math.abs(prevNetSales)) / Math.abs(prevNetSales)) * 100
    : undefined;

  if (revenueGrowth !== undefined && revenueGrowth > 50) {
    valuationWarnings.push({
      type: "info",
      title: "Kraftig tillväxt",
      description: `Omsättningen har ökat med ${Math.round(revenueGrowth)}% jämfört med föregående år.`,
      impact: "Historiska multiplar kan underskatta värdet. Framtida intjäningsförmåga kan motivera högre värdering.",
    });
  } else if (revenueGrowth !== undefined && revenueGrowth > 30) {
    valuationWarnings.push({
      type: "info",
      title: "Stark tillväxt",
      description: `Omsättningen har ökat med ${Math.round(revenueGrowth)}% jämfört med föregående år.`,
      impact: "Tillväxtbolag värderas ofta med hänsyn till framtida potential snarare än historiska resultat.",
    });
  }

  // 4. Revenue decline warning
  if (revenueGrowth !== undefined && revenueGrowth < -30) {
    valuationWarnings.push({
      type: "warning",
      title: "Kraftigt omsättningstapp",
      description: `Omsättningen har minskat med ${Math.abs(Math.round(revenueGrowth))}% jämfört med föregående år.`,
      impact: "Fallande omsättning kan indikera strukturella problem. Historiska multiplar kan överskatta värdet.",
    });
  }

  // 5. Crisis / distress detection (low equity ratio or negative working capital)
  const equityRatio = totalAssets > 0 ? (equity / totalAssets) * 100 : 0;
  if (equityRatio > 0 && equityRatio < 10 && equity > 0) {
    valuationWarnings.push({
      type: "warning",
      title: "Mycket låg soliditet",
      description: `Soliditeten är endast ${equityRatio.toFixed(1)}%.`,
      impact: "Hög skuldsättning ökar risken. Köpare kan kräva riskpremie eller förvärv kan kräva åtgärder.",
    });
  }

  // --- Substansvärde = justerat eget kapital ---
  const substansValue = adjustedEquity;

  // --- Market-adjusted substansvärde for property companies ---
  const buildingBookValue = buildings ?? 0;
  const isPropertyWithBuildings =
    activeProfile.id === "property" && buildingBookValue > 0;

  // Under K2, buildings are booked at cost minus depreciation.
  // Swedish property market values are typically 1.5–3× book value.
  const adjustedSubstansLow = isPropertyWithBuildings
    ? substansValue + buildingBookValue * 0.5
    : substansValue;
  const adjustedSubstansMid = isPropertyWithBuildings
    ? substansValue + buildingBookValue * 1.0
    : substansValue;
  const adjustedSubstansHigh = isPropertyWithBuildings
    ? substansValue + buildingBookValue * 2.0
    : substansValue;

  // --- Normaliserat resultat ---
  const normalizedEarnings =
    prevNetResult != null && prevNetResult !== 0
      ? (netResult + prevNetResult) / 2
      : netResult;

  // Avkastningsvärde: normaliserat resultat × P/E-multipel (3–7× för SME)
  const earningsLow = normalizedEarnings > 0 ? normalizedEarnings * 3 : 0;
  const earningsMid = normalizedEarnings > 0 ? normalizedEarnings * 5 : 0;
  const earningsHigh = normalizedEarnings > 0 ? normalizedEarnings * 7 : 0;

  // --- EBITDA ---
  const ebitda = operatingResult + Math.abs(depreciationAmortization);
  const ebitdaLow = ebitda > 0 ? ebitda * 4 : 0;
  const ebitdaMid = ebitda > 0 ? ebitda * 6 : 0;
  const ebitdaHigh = ebitda > 0 ? ebitda * 8 : 0;

  // --- Combined range ---
  const allValues = [
    isPropertyWithBuildings ? adjustedSubstansLow : substansValue,
    isPropertyWithBuildings ? adjustedSubstansHigh : substansValue,
    earningsLow,
    earningsHigh,
    ebitdaLow,
    ebitdaHigh,
  ].filter((v) => v > 0);
  const valuationLow = allValues.length > 0 ? Math.min(...allValues) : 0;
  const valuationHigh = allValues.length > 0 ? Math.max(...allValues) : 0;

  if (substansValue <= 0 && normalizedEarnings <= 0 && ebitda <= 0)
    return null;

  const isPrimary = (method: "substans" | "earnings" | "ebitda") =>
    activeProfile.primaryMethod === method;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calculator size={18} className="text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">
            Indikativ värdering
          </h3>
        </div>
        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
          {activeProfile.label}
        </span>
      </div>

      {/* Profile selector */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-blue-700">
            Ändra bolagsprofil:
            {isSavingProfile && <Spinner size="sm" className="inline ml-2" />}
          </span>
          {(Object.keys(PROFILES) as CompanyProfile[]).map((profileId) => {
            const p = PROFILES[profileId];
            const isActive = activeProfile.id === profileId;
            const isDetected = profileDetection.profile.id === profileId;
            return (
              <button
                key={profileId}
                disabled={isSavingProfile}
                onClick={() => {
                  if (isDetected) {
                    onProfileChange(null); // Reset to detected
                  } else {
                    onProfileChange(profileId);
                  }
                }}
                className={`px-2 py-1 text-xs rounded-full transition-all ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "bg-white text-blue-700 hover:bg-blue-100"
                } ${isDetected && !isActive ? "ring-1 ring-blue-400" : ""} ${isSavingProfile ? "opacity-50 cursor-not-allowed" : ""}`}
                title={p.description}
              >
                {p.label}
                {isDetected && !isActive && " (auto)"}
              </button>
            );
          })}
        </div>
        {isProfileOverridden && (
          <button
            disabled={isSavingProfile}
            onClick={() => onProfileChange(null)}
            className={`mt-2 text-xs text-blue-600 hover:text-blue-800 underline ${isSavingProfile ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            Återställ till automatisk profilering
          </button>
        )}
        <p className="mt-2 text-xs text-blue-600">
          <Info size={12} className="inline mr-1" />
          Profilen påverkar vilka analyser som visas och vilken värderingsmetod som rekommenderas.
        </p>
      </div>

      {/* Valuation warnings (loss-making, negative equity, growth, crisis) */}
      {valuationWarnings.length > 0 && (
        <div className="mb-4 space-y-2">
          {valuationWarnings.map((warning, idx) => {
            const colors = {
              danger: { bg: "bg-red-50", border: "border-red-200", title: "text-red-800", text: "text-red-700", icon: "text-red-600" },
              warning: { bg: "bg-amber-50", border: "border-amber-200", title: "text-amber-800", text: "text-amber-700", icon: "text-amber-600" },
              info: { bg: "bg-blue-50", border: "border-blue-200", title: "text-blue-800", text: "text-blue-700", icon: "text-blue-600" },
            }[warning.type];

            const Icon = warning.type === "danger" ? XCircle : warning.type === "warning" ? AlertTriangle : Info;

            return (
              <div key={idx} className={`p-3 ${colors.bg} border ${colors.border} rounded-lg`}>
                <div className="flex items-start gap-2">
                  <Icon size={16} className={`${colors.icon} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${colors.title}`}>
                      {warning.title}
                    </p>
                    <p className={`text-sm ${colors.text} mt-0.5`}>
                      {warning.description}
                    </p>
                    <p className={`text-xs ${colors.text} mt-1 opacity-80`}>
                      <strong>Påverkan på värdering:</strong> {warning.impact}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Property company: market-adjusted substansvärde */}
      {isPropertyWithBuildings && (
        <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-lg">
          <p className="text-sm text-violet-800 mb-2">
            <strong>Marknadsjusterat substansvärde.</strong> Byggnader är
            bokförda till {formatCompact(buildingBookValue)} (K2:
            anskaffningsvärde − avskrivningar). Marknadsvärdet är normalt
            betydligt högre.
          </p>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div className="text-center p-2 bg-white/60 rounded-lg">
              <p className="text-[10px] text-violet-600">Försiktigt (1,5×)</p>
              <p className="text-sm font-bold text-violet-900">
                {formatCompact(adjustedSubstansLow)}
              </p>
            </div>
            <div className="text-center p-2 bg-white/60 rounded-lg ring-1 ring-violet-300">
              <p className="text-[10px] text-violet-600">Troligt (2×)</p>
              <p className="text-sm font-bold text-violet-900">
                {formatCompact(adjustedSubstansMid)}
              </p>
            </div>
            <div className="text-center p-2 bg-white/60 rounded-lg">
              <p className="text-[10px] text-violet-600">Optimistiskt (3×)</p>
              <p className="text-sm font-bold text-violet-900">
                {formatCompact(adjustedSubstansHigh)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Substansvärde */}
        <div
          className={`p-4 rounded-xl ${
            isPrimary("substans") && !isPropertyWithBuildings
              ? "bg-primary-50 ring-1 ring-primary-200"
              : "bg-gray-50"
          }`}
        >
          <p className="text-xs text-gray-500 mb-1">
            Substansvärde{isPrimary("substans") && !isPropertyWithBuildings && " ★"}
          </p>
          <p className="text-xl font-bold text-gray-900">
            {formatCompact(substansValue)}
          </p>
          <p className="text-[11px] text-gray-400 mt-1">
            {isPropertyWithBuildings
              ? "Bokfört (ej marknadsjusterat)"
              : "Justerat eget kapital"}
          </p>
        </div>

        {/* Avkastningsvärde */}
        <div
          className={`p-4 rounded-xl ${
            isPrimary("earnings")
              ? "bg-primary-50 ring-1 ring-primary-200"
              : "bg-gray-50"
          }`}
        >
          <p className="text-xs text-gray-500 mb-1">
            Avkastningsvärde{isPrimary("earnings") && " ★"}
          </p>
          {earningsMid > 0 ? (
            <>
              <p className="text-xl font-bold text-gray-900">
                {formatCompact(earningsMid)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1">
                {formatCompact(earningsLow)} — {formatCompact(earningsHigh)}{" "}
                (3–7× resultat)
              </p>
            </>
          ) : (
            <>
              <p className="text-xl font-bold text-gray-400">N/A</p>
              <p className="text-[11px] text-gray-400 mt-1">
                Kräver positivt resultat
              </p>
            </>
          )}
        </div>

        {/* EV/EBITDA */}
        <div
          className={`p-4 rounded-xl ${
            isPrimary("ebitda")
              ? "bg-primary-50 ring-1 ring-primary-200"
              : "bg-gray-50"
          }`}
        >
          <p className="text-xs text-gray-500 mb-1">
            EV/EBITDA{isPrimary("ebitda") && " ★"}
          </p>
          {ebitdaMid > 0 ? (
            <>
              <p className="text-xl font-bold text-gray-900">
                {formatCompact(ebitdaMid)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1">
                {formatCompact(ebitdaLow)} — {formatCompact(ebitdaHigh)}{" "}
                (4–8× EBITDA)
              </p>
            </>
          ) : (
            <>
              <p className="text-xl font-bold text-gray-400">N/A</p>
              <p className="text-[11px] text-gray-400 mt-1">
                Kräver positiv EBITDA
              </p>
            </>
          )}
        </div>

        {/* EBITDA value */}
        <div className="p-4 bg-gray-50 rounded-xl">
          <p className="text-xs text-gray-500 mb-1">EBITDA</p>
          <p
            className={`text-xl font-bold ${
              ebitda > 0 ? "text-gray-900" : "text-gray-400"
            }`}
          >
            {ebitda > 0 ? formatCompact(ebitda) : formatCompact(0)}
          </p>
          <p className="text-[11px] text-gray-400 mt-1">
            Rörelseresultat + avskrivningar
          </p>
        </div>
      </div>

      {/* Combined range bar */}
      {valuationHigh > 0 && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Indikativt spann</span>
            <span>
              {formatCompact(valuationLow)} — {formatCompact(valuationHigh)}
            </span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden relative">
            {/* Substansvärde marker (or adjusted range for property) */}
            {(isPropertyWithBuildings ? adjustedSubstansLow : substansValue) >
              0 && (
              <div
                className="absolute h-full bg-violet-200 rounded-full"
                style={{
                  left: 0,
                  width: `${Math.min(
                    ((isPropertyWithBuildings
                      ? adjustedSubstansHigh
                      : substansValue) /
                      valuationHigh) *
                      100,
                    100
                  )}%`,
                }}
              />
            )}
            {/* Earnings range */}
            {earningsLow > 0 && (
              <div
                className="absolute h-full bg-primary-400 rounded-full"
                style={{
                  left: `${Math.min(
                    (earningsLow / valuationHigh) * 100,
                    100
                  )}%`,
                  width: `${Math.min(
                    ((earningsHigh - earningsLow) / valuationHigh) * 100,
                    100 - (earningsLow / valuationHigh) * 100
                  )}%`,
                }}
              />
            )}
            {/* EV/EBITDA range */}
            {ebitdaLow > 0 && (
              <div
                className="absolute h-full bg-amber-300 rounded-full opacity-60"
                style={{
                  left: `${Math.min(
                    (ebitdaLow / valuationHigh) * 100,
                    100
                  )}%`,
                  width: `${Math.min(
                    ((ebitdaHigh - ebitdaLow) / valuationHigh) * 100,
                    100 - (ebitdaLow / valuationHigh) * 100
                  )}%`,
                }}
              />
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-gray-400 mt-1">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-violet-200 inline-block" />
              Substansvärde{isPropertyWithBuildings && " (justerat)"}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-primary-400 inline-block" />
              Avkastningsvärde (3–7×)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-300 inline-block" />
              EV/EBITDA (4–8×)
            </span>
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-3">
        ★ = rekommenderad primärmetod för {activeProfile.label.toLowerCase()}.{" "}
        {isPropertyWithBuildings && (
          <>
            Marknadsjusterat substansvärde adderar uppskattat övervärde på
            byggnader (1,5–3× bokfört värde).{" "}
          </>
        )}
        Indikativ uppskattning — ersätter inte professionell
        företagsvärdering.
      </p>
    </div>
  );
}

function EfficiencyRatios({
  netResult,
  operatingResult,
  equity,
  totalAssets,
  netSales,
  currentAssets,
  shortTermLiabilities,
  inventory,
}: {
  netResult: number;
  operatingResult: number;
  equity: number;
  totalAssets: number;
  netSales: number;
  currentAssets: number;
  shortTermLiabilities: number;
  inventory: number;
}) {
  // ROE = net result / equity
  const roe = equity > 0 ? (netResult / equity) * 100 : null;
  // Return on total capital = operating result / total assets
  const rotc = totalAssets > 0 ? (operatingResult / totalAssets) * 100 : null;
  // Kapitalomsättningshastighet = net sales / total assets
  const capitalTurnover = totalAssets > 0 ? netSales / totalAssets : null;
  // Rörelsekapital = current assets - short-term liabilities
  const workingCapital = currentAssets - shortTermLiabilities;
  // Snabblikviditet (quick ratio) = (current assets - inventory) / short-term liabilities
  const quickRatio =
    shortTermLiabilities > 0
      ? (currentAssets - inventory) / shortTermLiabilities
      : null;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <Gauge size={18} className="text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">
          Effektivitetsnyckeltal
        </h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="p-3 bg-gray-50 rounded-xl text-center">
          <p className="text-[11px] text-gray-500 mb-1">Avk. eget kap.</p>
          <p
            className={`text-lg font-bold ${
              roe != null && roe >= 15
                ? "text-emerald-700"
                : roe != null && roe >= 5
                  ? "text-amber-700"
                  : "text-red-700"
            }`}
          >
            {roe != null ? formatPercent(roe) : "N/A"}
          </p>
          <p className="text-[10px] text-gray-400">ROE</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-xl text-center">
          <p className="text-[11px] text-gray-500 mb-1">Avk. totalt kap.</p>
          <p
            className={`text-lg font-bold ${
              rotc != null && rotc >= 10
                ? "text-emerald-700"
                : rotc != null && rotc >= 5
                  ? "text-amber-700"
                  : "text-red-700"
            }`}
          >
            {rotc != null ? formatPercent(rotc) : "N/A"}
          </p>
          <p className="text-[10px] text-gray-400">ROTC</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-xl text-center">
          <p className="text-[11px] text-gray-500 mb-1">Kap.omsättning</p>
          <p className="text-lg font-bold text-gray-900">
            {capitalTurnover != null
              ? capitalTurnover.toLocaleString("sv-SE", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                }) + "×"
              : "N/A"}
          </p>
          <p className="text-[10px] text-gray-400">Omsättning / Tillgångar</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-xl text-center">
          <p className="text-[11px] text-gray-500 mb-1">Rörelsekapital</p>
          <p
            className={`text-lg font-bold ${
              workingCapital >= 0 ? "text-emerald-700" : "text-red-700"
            }`}
          >
            {formatCompact(workingCapital)}
          </p>
          <p className="text-[10px] text-gray-400">OT − KS</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-xl text-center">
          <p className="text-[11px] text-gray-500 mb-1">Snabblikviditet</p>
          <p
            className={`text-lg font-bold ${
              quickRatio != null && quickRatio >= 1
                ? "text-emerald-700"
                : quickRatio != null && quickRatio >= 0.7
                  ? "text-amber-700"
                  : "text-red-700"
            }`}
          >
            {quickRatio != null
              ? quickRatio.toLocaleString("sv-SE", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })
              : "N/A"}
          </p>
          <p className="text-[10px] text-gray-400">Quick ratio</p>
        </div>
      </div>
    </div>
  );
}

function BreakEvenIndicator({
  netSales,
  operatingResult,
}: {
  netSales: number;
  operatingResult: number;
}) {
  if (netSales <= 0 || operatingResult <= 0) return null;

  // Säkerhetsmarginal = operatingResult / netSales × 100
  // Visar hur mycket omsättningen kan sjunka innan rörelseresultatet blir 0
  const safetyMargin = (operatingResult / netSales) * 100;
  const breakEvenRevenue = netSales - operatingResult;

  const marginLevel: HealthLevel =
    safetyMargin >= 20 ? "good" : safetyMargin >= 10 ? "warning" : "danger";
  const colors = LEVEL_COLORS[marginLevel];

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <Target size={18} className="text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">
          Break-even & säkerhetsmarginal
        </h3>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-gray-500 mb-1">Säkerhetsmarginal</p>
          <p className={`text-2xl font-bold ${colors.text}`}>
            {safetyMargin.toLocaleString("sv-SE", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
            %
          </p>
          <p className="text-[11px] text-gray-400 mt-1">
            Omsättningen kan sjunka{" "}
            {safetyMargin.toLocaleString("sv-SE", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
            % innan förlust uppstår
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Break-even omsättning</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCompact(breakEvenRevenue)}
          </p>
          <p className="text-[11px] text-gray-400 mt-1">
            Lägsta omsättning för nollresultat
          </p>
        </div>
      </div>
      {/* Visual bar */}
      <div className="mt-4">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>Break-even</span>
          <span>Nuvarande omsättning</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden relative">
          <div
            className="h-full bg-gray-300 rounded-l-full absolute"
            style={{
              width: `${Math.min((breakEvenRevenue / netSales) * 100, 100)}%`,
            }}
          />
          <div
            className={`h-full ${colors.bg} rounded-r-full absolute`}
            style={{
              left: `${Math.min((breakEvenRevenue / netSales) * 100, 100)}%`,
              width: `${Math.min(safetyMargin, 100 - (breakEvenRevenue / netSales) * 100)}%`,
            }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>{formatCompact(breakEvenRevenue)}</span>
          <span className="font-medium">{formatCompact(netSales)}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Altman Z-Score (Bankruptcy Prediction)
// ---------------------------------------------------------------------------

interface ZScoreResult {
  score: number;
  zone: "safe" | "grey" | "distress";
  components: {
    label: string;
    value: number;
    weight: number;
    contribution: number;
  }[];
}

function calculateAltmanZScore({
  workingCapital,
  totalAssets,
  retainedEarnings,
  ebit,
  marketValueEquity,
  bookValueDebt,
  sales,
}: {
  workingCapital: number;
  totalAssets: number;
  retainedEarnings: number;
  ebit: number;
  marketValueEquity: number; // For private companies, use book value
  bookValueDebt: number;
  sales: number;
}): ZScoreResult | null {
  if (totalAssets <= 0) return null;

  // Altman Z-Score formula for private companies (Z'-score):
  // Z' = 0.717×X1 + 0.847×X2 + 3.107×X3 + 0.420×X4 + 0.998×X5
  const x1 = workingCapital / totalAssets;
  const x2 = retainedEarnings / totalAssets;
  const x3 = ebit / totalAssets;
  const x4 = bookValueDebt > 0 ? marketValueEquity / bookValueDebt : 10; // Cap at 10 if no debt
  const x5 = sales / totalAssets;

  const weights = { x1: 0.717, x2: 0.847, x3: 3.107, x4: 0.42, x5: 0.998 };

  const score =
    weights.x1 * x1 +
    weights.x2 * x2 +
    weights.x3 * x3 +
    weights.x4 * Math.min(x4, 10) +
    weights.x5 * x5;

  // Z'-score interpretation for private companies:
  // > 2.9: Safe zone
  // 1.23 - 2.9: Grey zone
  // < 1.23: Distress zone
  const zone: "safe" | "grey" | "distress" =
    score > 2.9 ? "safe" : score > 1.23 ? "grey" : "distress";

  return {
    score,
    zone,
    components: [
      { label: "Rörelsekapital/Tillgångar", value: x1, weight: weights.x1, contribution: weights.x1 * x1 },
      { label: "Balanserat resultat/Tillgångar", value: x2, weight: weights.x2, contribution: weights.x2 * x2 },
      { label: "EBIT/Tillgångar", value: x3, weight: weights.x3, contribution: weights.x3 * x3 },
      { label: "EK/Skulder", value: Math.min(x4, 10), weight: weights.x4, contribution: weights.x4 * Math.min(x4, 10) },
      { label: "Omsättning/Tillgångar", value: x5, weight: weights.x5, contribution: weights.x5 * x5 },
    ],
  };
}

function AltmanZScoreCard({
  workingCapital,
  totalAssets,
  retainedEarnings,
  ebit,
  equity,
  totalDebt,
  sales,
}: {
  workingCapital: number;
  totalAssets: number;
  retainedEarnings: number;
  ebit: number;
  equity: number;
  totalDebt: number;
  sales: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const result = calculateAltmanZScore({
    workingCapital,
    totalAssets,
    retainedEarnings,
    ebit,
    marketValueEquity: equity, // Use book value for private companies
    bookValueDebt: totalDebt,
    sales,
  });

  if (!result) return null;

  const zoneColors = {
    safe: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-800" },
    grey: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-800" },
    distress: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-100 text-red-800" },
  };

  const colors = zoneColors[result.zone];
  const zoneLabel = { safe: "Säker zon", grey: "Grå zon", distress: "Riskzon" }[result.zone];

  return (
    <div className={`card ${colors.bg} ${colors.border} border`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Shield size={18} className={colors.text} />
            <h3 className="text-sm font-semibold text-gray-900">
              Altman Z-Score
            </h3>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${colors.badge}`}>
            {zoneLabel}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xl font-bold ${colors.text}`}>
            {result.score.toFixed(2)}
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          <p className={`text-sm ${colors.text}`}>
            {result.zone === "safe" && "Bolaget visar stark finansiell hälsa med låg konkursrisk."}
            {result.zone === "grey" && "Bolaget befinner sig i gråzonen — förhöjd uppmärksamhet krävs."}
            {result.zone === "distress" && "Bolaget visar tecken på finansiell stress — hög konkursrisk."}
          </p>

          <div className="space-y-2">
            {result.components.map((comp, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{comp.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">
                    {comp.value.toFixed(2)} × {comp.weight}
                  </span>
                  <span className={`font-medium ${comp.contribution >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {comp.contribution >= 0 ? "+" : ""}{comp.contribution.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t border-gray-200">
            <div className="flex justify-between text-xs text-gray-500">
              <span>Riskzon: &lt;1.23</span>
              <span>Grå zon: 1.23–2.9</span>
              <span>Säker: &gt;2.9</span>
            </div>
            <div className="mt-1 h-2 bg-gradient-to-r from-red-400 via-amber-400 to-emerald-400 rounded-full relative">
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-gray-800 rounded-full shadow"
                style={{ left: `${Math.min(Math.max((result.score / 5) * 100, 2), 98)}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DuPont Analysis (ROE Breakdown)
// ---------------------------------------------------------------------------

function DuPontAnalysis({
  netResult,
  netSales,
  totalAssets,
  equity,
}: {
  netResult: number;
  netSales: number;
  totalAssets: number;
  equity: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (equity <= 0 || totalAssets <= 0 || netSales <= 0) return null;

  // DuPont formula: ROE = Net Profit Margin × Asset Turnover × Equity Multiplier
  const netProfitMargin = netResult / netSales;
  const assetTurnover = netSales / totalAssets;
  const equityMultiplier = totalAssets / equity;
  const roe = netResult / equity;

  const formatPct = (v: number) => `${(v * 100).toFixed(1)}%`;
  const formatX = (v: number) => `${v.toFixed(2)}×`;

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Target size={18} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            DuPont-analys
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-lg font-bold ${roe >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            ROE: {formatPct(roe)}
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            DuPont-analysen bryter ner avkastning på eget kapital (ROE) i tre
            komponenter för att identifiera var värde skapas eller förstörs.
          </p>

          {/* Visual formula */}
          <div className="flex items-center justify-center gap-2 py-4 bg-gray-50 rounded-lg">
            <div className="text-center px-3">
              <p className="text-xs text-gray-500 mb-1">Nettomarginal</p>
              <p className={`text-lg font-bold ${netProfitMargin >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {formatPct(netProfitMargin)}
              </p>
            </div>
            <span className="text-gray-400 text-xl">×</span>
            <div className="text-center px-3">
              <p className="text-xs text-gray-500 mb-1">Kapitalomsättning</p>
              <p className="text-lg font-bold text-blue-600">
                {formatX(assetTurnover)}
              </p>
            </div>
            <span className="text-gray-400 text-xl">×</span>
            <div className="text-center px-3">
              <p className="text-xs text-gray-500 mb-1">Hävstång</p>
              <p className="text-lg font-bold text-violet-600">
                {formatX(equityMultiplier)}
              </p>
            </div>
            <span className="text-gray-400 text-xl">=</span>
            <div className="text-center px-3">
              <p className="text-xs text-gray-500 mb-1">ROE</p>
              <p className={`text-lg font-bold ${roe >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {formatPct(roe)}
              </p>
            </div>
          </div>

          {/* Interpretation */}
          <div className="grid md:grid-cols-3 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-1">
                Nettomarginal {formatPct(netProfitMargin)}
              </p>
              <p className="text-xs text-gray-500">
                {netProfitMargin >= 0.1
                  ? "Stark lönsamhet per omsättningskrona."
                  : netProfitMargin >= 0.03
                    ? "Acceptabel marginal."
                    : netProfitMargin >= 0
                      ? "Låg marginal — kostnadseffektivisering behövs."
                      : "Negativ marginal — förlust på varje såld krona."}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-1">
                Kapitalomsättning {formatX(assetTurnover)}
              </p>
              <p className="text-xs text-gray-500">
                {assetTurnover >= 1.5
                  ? "Effektiv användning av tillgångar."
                  : assetTurnover >= 0.8
                    ? "Normal kapitalomsättning."
                    : "Låg omsättning — tillgångar utnyttjas ineffektivt."}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-1">
                Finansiell hävstång {formatX(equityMultiplier)}
              </p>
              <p className="text-xs text-gray-500">
                {equityMultiplier >= 4
                  ? "Hög skuldsättning — ökad risk men potential."
                  : equityMultiplier >= 2
                    ? "Måttlig hävstång."
                    : "Låg hävstång — konservativ finansiering."}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Working Capital Cycle (DSO, DPO, Inventory Turnover)
// ---------------------------------------------------------------------------

function WorkingCapitalCycle({
  accountsReceivable,
  accountsPayable,
  inventory,
  netSales,
  costOfGoodsSold,
}: {
  accountsReceivable: number;
  accountsPayable: number;
  inventory: number;
  netSales: number;
  costOfGoodsSold: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const absNetSales = Math.abs(netSales);
  const absCOGS = Math.abs(costOfGoodsSold);

  if (absNetSales <= 0) return null;

  // Days Sales Outstanding (DSO) = (Accounts Receivable / Net Sales) × 365
  const dso = absNetSales > 0 ? (accountsReceivable / absNetSales) * 365 : 0;

  // Days Payable Outstanding (DPO) = (Accounts Payable / COGS) × 365
  const dpo = absCOGS > 0 ? (accountsPayable / absCOGS) * 365 : 0;

  // Days Inventory Outstanding (DIO) = (Inventory / COGS) × 365
  const dio = absCOGS > 0 ? (inventory / absCOGS) * 365 : 0;

  // Cash Conversion Cycle = DSO + DIO - DPO
  const ccc = dso + dio - dpo;

  // Inventory turnover = COGS / Inventory
  const inventoryTurnover = inventory > 0 ? absCOGS / inventory : 0;

  const formatDays = (d: number) => `${Math.round(d)} dagar`;

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Wallet size={18} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Rörelsekapitalcykel
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-lg font-bold ${ccc <= 30 ? "text-emerald-600" : ccc <= 60 ? "text-amber-600" : "text-red-600"}`}>
            {Math.round(ccc)} dagar
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            Rörelsekapitalcykeln visar hur lång tid det tar att omvandla
            investeringar i lager och kundfordringar till kassaflöde.
          </p>

          {/* Visual cycle */}
          <div className="flex items-center justify-center gap-1 py-4 bg-gray-50 rounded-lg text-sm">
            <div className="text-center px-2">
              <p className="text-xs text-gray-500">Kundfordr.</p>
              <p className="font-bold text-blue-600">{Math.round(dso)}d</p>
            </div>
            <span className="text-gray-400">+</span>
            <div className="text-center px-2">
              <p className="text-xs text-gray-500">Lager</p>
              <p className="font-bold text-violet-600">{Math.round(dio)}d</p>
            </div>
            <span className="text-gray-400">−</span>
            <div className="text-center px-2">
              <p className="text-xs text-gray-500">Lev.skuld</p>
              <p className="font-bold text-amber-600">{Math.round(dpo)}d</p>
            </div>
            <span className="text-gray-400">=</span>
            <div className="text-center px-2">
              <p className="text-xs text-gray-500">Cykel</p>
              <p className={`font-bold ${ccc <= 30 ? "text-emerald-600" : ccc <= 60 ? "text-amber-600" : "text-red-600"}`}>
                {Math.round(ccc)}d
              </p>
            </div>
          </div>

          {/* Metrics grid */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-1">
                <p className="text-xs font-medium text-gray-700">
                  Kundfordringsdagar (DSO)
                </p>
                <p className="text-sm font-bold text-blue-600">{formatDays(dso)}</p>
              </div>
              <p className="text-xs text-gray-500">
                {dso <= 30 ? "Utmärkt — snabb inkassering." : dso <= 45 ? "Normalt." : dso <= 60 ? "Något långsamt." : "Lång kredittid — likviditetsrisk."}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-1">
                <p className="text-xs font-medium text-gray-700">
                  Leverantörsskulddagar (DPO)
                </p>
                <p className="text-sm font-bold text-amber-600">{formatDays(dpo)}</p>
              </div>
              <p className="text-xs text-gray-500">
                {dpo >= 45 ? "Bra — utnyttjar leverantörskredit." : dpo >= 30 ? "Normalt." : "Kort kredittid — binder kapital."}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-1">
                <p className="text-xs font-medium text-gray-700">
                  Lagerdagar (DIO)
                </p>
                <p className="text-sm font-bold text-violet-600">{formatDays(dio)}</p>
              </div>
              <p className="text-xs text-gray-500">
                {dio <= 30 ? "Effektiv lagerhantering." : dio <= 60 ? "Normalt." : dio <= 90 ? "Något högt lager." : "Högt lager — bundet kapital."}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-1">
                <p className="text-xs font-medium text-gray-700">
                  Lageromsättningshastighet
                </p>
                <p className="text-sm font-bold text-gray-700">
                  {inventoryTurnover > 0 ? `${inventoryTurnover.toFixed(1)}×/år` : "N/A"}
                </p>
              </div>
              <p className="text-xs text-gray-500">
                {inventoryTurnover >= 6 ? "Effektivt." : inventoryTurnover >= 3 ? "Normalt." : inventoryTurnover > 0 ? "Låg omsättning." : "Inget lager."}
              </p>
            </div>
          </div>

          {/* CCC interpretation */}
          <div className={`p-3 rounded-lg ${ccc <= 30 ? "bg-emerald-50" : ccc <= 60 ? "bg-amber-50" : "bg-red-50"}`}>
            <p className={`text-sm ${ccc <= 30 ? "text-emerald-800" : ccc <= 60 ? "text-amber-800" : "text-red-800"}`}>
              <strong>Kassaomvandlingscykel: {Math.round(ccc)} dagar.</strong>{" "}
              {ccc <= 0
                ? "Negativt är utmärkt — ni finansieras av leverantörerna!"
                : ccc <= 30
                  ? "Kort cykel — god likviditet."
                  : ccc <= 60
                    ? "Normal cykel — fungerande rörelsekapital."
                    : "Lång cykel — kapital binds i verksamheten."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Interest Coverage & Debt Analysis
// ---------------------------------------------------------------------------

function DebtAnalysis({
  operatingResult,
  interestExpense,
  totalDebt,
  equity,
  ebitda,
}: {
  operatingResult: number;
  interestExpense: number;
  totalDebt: number;
  equity: number;
  ebitda: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const absInterest = Math.abs(interestExpense);

  // Interest coverage ratio = EBIT / Interest expense
  const interestCoverage = absInterest > 0 ? operatingResult / absInterest : null;

  // Debt-to-equity ratio
  const debtToEquity = equity > 0 ? totalDebt / equity : null;

  // Debt-to-EBITDA ratio
  const debtToEbitda = ebitda > 0 ? totalDebt / ebitda : null;

  // If no debt and no interest, show positive message
  if (totalDebt <= 0 && absInterest <= 0) {
    return (
      <div className="card bg-emerald-50 border border-emerald-200">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-900">Skuldanalys</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
            Skuldfritt
          </span>
        </div>
        <p className="mt-2 text-sm text-emerald-700">
          Bolaget har inga räntebärande skulder — stark finansiell position.
        </p>
      </div>
    );
  }

  const coverageStatus =
    interestCoverage === null
      ? "neutral"
      : interestCoverage >= 5
        ? "safe"
        : interestCoverage >= 2.5
          ? "warning"
          : "danger";

  const coverageColors = {
    safe: "text-emerald-600",
    warning: "text-amber-600",
    danger: "text-red-600",
    neutral: "text-gray-600",
  };

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Banknote size={18} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Skuld- & ränteanalys
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {interestCoverage !== null && (
            <span className={`text-lg font-bold ${coverageColors[coverageStatus]}`}>
              {interestCoverage.toFixed(1)}× täckning
            </span>
          )}
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            {interestCoverage !== null && (
              <div className={`p-3 rounded-lg ${coverageStatus === "safe" ? "bg-emerald-50" : coverageStatus === "warning" ? "bg-amber-50" : "bg-red-50"}`}>
                <p className="text-xs font-medium text-gray-700 mb-1">
                  Räntetäckningsgrad
                </p>
                <p className={`text-xl font-bold ${coverageColors[coverageStatus]}`}>
                  {interestCoverage.toFixed(1)}×
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {interestCoverage >= 5
                    ? "Stark — god marginal för räntekostnader."
                    : interestCoverage >= 2.5
                      ? "Acceptabel — viss buffert."
                      : interestCoverage >= 1
                        ? "Svag — liten marginal."
                        : "Kritisk — räntekostnaderna överstiger rörelseresultatet."}
                </p>
              </div>
            )}

            {debtToEquity !== null && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  Skuldsättningsgrad
                </p>
                <p className={`text-xl font-bold ${debtToEquity <= 1 ? "text-emerald-600" : debtToEquity <= 2 ? "text-amber-600" : "text-red-600"}`}>
                  {debtToEquity.toFixed(2)}×
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {debtToEquity <= 0.5
                    ? "Låg skuldsättning."
                    : debtToEquity <= 1
                      ? "Balanserad."
                      : debtToEquity <= 2
                        ? "Måttlig skuldsättning."
                        : "Hög skuldsättning."}
                </p>
              </div>
            )}

            {debtToEbitda !== null && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  Skuld/EBITDA
                </p>
                <p className={`text-xl font-bold ${debtToEbitda <= 2 ? "text-emerald-600" : debtToEbitda <= 4 ? "text-amber-600" : "text-red-600"}`}>
                  {debtToEbitda.toFixed(1)}×
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {debtToEbitda <= 2
                    ? "Hanterbar skuldbörda."
                    : debtToEbitda <= 4
                      ? "Normal för belånade bolag."
                      : "Hög — tar lång tid att betala av."}
                </p>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500">
            Räntetäckningsgrad = Rörelseresultat / Räntekostnader. Visar hur
            många gånger bolaget kan betala sina räntekostnader med
            rörelseresultatet. Under 2,5× anses riskabelt.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sustainable Growth Rate
// ---------------------------------------------------------------------------

function SustainableGrowthRate({
  netResult,
  equity,
  proposedDividend,
}: {
  netResult: number;
  equity: number;
  proposedDividend: number;
}) {
  if (equity <= 0 || netResult <= 0) return null;

  const roe = netResult / equity;
  const retentionRatio = proposedDividend > 0 && netResult > 0
    ? Math.max(0, (netResult - proposedDividend) / netResult)
    : 1; // If no dividend, 100% retained

  // Sustainable growth rate = ROE × Retention ratio
  const sgr = roe * retentionRatio;

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Hållbar tillväxttakt
          </h3>
        </div>
        <span className={`text-lg font-bold ${sgr >= 0.1 ? "text-emerald-600" : sgr >= 0.05 ? "text-blue-600" : "text-gray-600"}`}>
          {formatPercent(sgr * 100)}
        </span>
      </div>

      <div className="mt-3 space-y-2">
        <p className="text-sm text-gray-600">
          Den maximala tillväxttakt bolaget kan uppnå utan extern finansiering.
        </p>

        <div className="flex items-center justify-center gap-2 py-3 bg-gray-50 rounded-lg text-sm">
          <div className="text-center px-3">
            <p className="text-xs text-gray-500">ROE</p>
            <p className="font-bold text-blue-600">{formatPercent(roe * 100)}</p>
          </div>
          <span className="text-gray-400">×</span>
          <div className="text-center px-3">
            <p className="text-xs text-gray-500">Återinvestering</p>
            <p className="font-bold text-violet-600">{formatPercent(retentionRatio * 100)}</p>
          </div>
          <span className="text-gray-400">=</span>
          <div className="text-center px-3">
            <p className="text-xs text-gray-500">Max tillväxt</p>
            <p className={`font-bold ${sgr >= 0.1 ? "text-emerald-600" : "text-blue-600"}`}>
              {formatPercent(sgr * 100)}
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-500">
          {sgr >= 0.15
            ? "Hög tillväxtkapacitet — kan växa snabbt organiskt."
            : sgr >= 0.08
              ? "God tillväxtkapacitet."
              : sgr >= 0.03
                ? "Måttlig tillväxtkapacitet — extern finansiering kan behövas vid expansion."
                : "Låg tillväxtkapacitet — begränsad organisk tillväxt."}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Liquidation Value Analysis
// ---------------------------------------------------------------------------

function LiquidationValueAnalysis({
  cashAndBank,
  accountsReceivable,
  inventory,
  otherCurrentAssets,
  buildings,
  machineryAndEquipment,
  financialAssets,
  intangibleAssets,
  equity,
  isInDistress,
}: {
  cashAndBank: number;
  accountsReceivable: number;
  inventory: number;
  otherCurrentAssets: number;
  buildings: number;
  machineryAndEquipment: number;
  financialAssets: number;
  intangibleAssets: number;
  equity: number;
  isInDistress: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Only show for distressed companies or expand on demand
  if (!isInDistress && !isExpanded) {
    return null;
  }

  // Recovery rates in liquidation (conservative estimates)
  const recoveryRates = {
    cashAndBank: 1.0,           // 100%
    accountsReceivable: 0.7,    // 70%
    inventory: 0.5,             // 50%
    otherCurrentAssets: 0.3,    // 30%
    buildings: 0.8,             // 80% (real estate holds value)
    machineryAndEquipment: 0.3, // 30%
    financialAssets: 0.9,       // 90%
    intangibleAssets: 0.0,      // 0% (usually worthless)
  };

  const liquidationValues = {
    cashAndBank: cashAndBank * recoveryRates.cashAndBank,
    accountsReceivable: accountsReceivable * recoveryRates.accountsReceivable,
    inventory: inventory * recoveryRates.inventory,
    otherCurrentAssets: otherCurrentAssets * recoveryRates.otherCurrentAssets,
    buildings: buildings * recoveryRates.buildings,
    machineryAndEquipment: machineryAndEquipment * recoveryRates.machineryAndEquipment,
    financialAssets: financialAssets * recoveryRates.financialAssets,
    intangibleAssets: intangibleAssets * recoveryRates.intangibleAssets,
  };

  const totalLiquidationValue = Object.values(liquidationValues).reduce((a, b) => a + b, 0);
  const totalBookValue =
    cashAndBank + accountsReceivable + inventory + otherCurrentAssets +
    buildings + machineryAndEquipment + financialAssets + intangibleAssets;

  const recoveryRate = totalBookValue > 0 ? totalLiquidationValue / totalBookValue : 0;

  const assets = [
    { name: "Kassa & bank", book: cashAndBank, rate: recoveryRates.cashAndBank, liquid: liquidationValues.cashAndBank },
    { name: "Kundfordringar", book: accountsReceivable, rate: recoveryRates.accountsReceivable, liquid: liquidationValues.accountsReceivable },
    { name: "Varulager", book: inventory, rate: recoveryRates.inventory, liquid: liquidationValues.inventory },
    { name: "Övr. omsättn.tillg.", book: otherCurrentAssets, rate: recoveryRates.otherCurrentAssets, liquid: liquidationValues.otherCurrentAssets },
    { name: "Byggnader & mark", book: buildings, rate: recoveryRates.buildings, liquid: liquidationValues.buildings },
    { name: "Maskiner & invent.", book: machineryAndEquipment, rate: recoveryRates.machineryAndEquipment, liquid: liquidationValues.machineryAndEquipment },
    { name: "Finansiella tillg.", book: financialAssets, rate: recoveryRates.financialAssets, liquid: liquidationValues.financialAssets },
    { name: "Immateriella tillg.", book: intangibleAssets, rate: recoveryRates.intangibleAssets, liquid: liquidationValues.intangibleAssets },
  ].filter(a => a.book > 0);

  return (
    <div className="card bg-gray-50 border border-gray-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Scale size={18} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Likvidationsvärde
          </h3>
          {isInDistress && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">
              Relevant vid kris
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-lg font-bold ${totalLiquidationValue >= equity ? "text-emerald-600" : "text-red-600"}`} title={formatCurrency(totalLiquidationValue)}>
            {formatCompact(totalLiquidationValue)}
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            Uppskattat värde vid avveckling, baserat på typiska återvinningsgrader.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">Tillgång</th>
                  <th className="pb-2 text-right">Bokfört</th>
                  <th className="pb-2 text-right">Återvinning</th>
                  <th className="pb-2 text-right">Likv.värde</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {assets.map((asset, idx) => (
                  <tr key={idx}>
                    <td className="py-2 text-gray-700">{asset.name}</td>
                    <td className="py-2 text-right text-gray-600">{formatCompact(asset.book)}</td>
                    <td className="py-2 text-right text-gray-500">{Math.round(asset.rate * 100)}%</td>
                    <td className="py-2 text-right font-medium text-gray-800">{formatCompact(asset.liquid)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td className="pt-2 font-medium text-gray-900">Totalt</td>
                  <td className="pt-2 text-right text-gray-600">{formatCompact(totalBookValue)}</td>
                  <td className="pt-2 text-right text-gray-500">{Math.round(recoveryRate * 100)}%</td>
                  <td className="pt-2 text-right font-bold text-gray-900">{formatCompact(totalLiquidationValue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className={`p-3 rounded-lg ${totalLiquidationValue >= equity ? "bg-emerald-50" : "bg-red-50"}`}>
            <p className={`text-sm ${totalLiquidationValue >= equity ? "text-emerald-800" : "text-red-800"}`}>
              {totalLiquidationValue >= equity
                ? `Likvidationsvärdet överstiger bokfört eget kapital med ${formatCompact(totalLiquidationValue - equity)}.`
                : `Likvidationsvärdet understiger bokfört eget kapital med ${formatCompact(equity - totalLiquidationValue)}.`}
            </p>
          </div>

          <p className="text-xs text-gray-400">
            Återvinningsgrader är schablonmässiga och varierar beroende på bransch,
            tillgångarnas skick och marknadsförhållanden.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Risk Indicators (Intangibles, Tax Efficiency, Operating Leverage)
// ---------------------------------------------------------------------------

function RiskIndicators({
  intangibleAssets,
  totalAssets,
  goodwill,
  taxExpense,
  resultBeforeTax,
  operatingResult,
  netSales,
  variableCostsRatio,
}: {
  intangibleAssets: number;
  totalAssets: number;
  goodwill: number;
  taxExpense: number;
  resultBeforeTax: number;
  operatingResult: number;
  netSales: number;
  variableCostsRatio: number; // Estimated variable costs / net sales
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const absNetSales = Math.abs(netSales);

  // Intangible assets risk
  const intangibleRatio = totalAssets > 0 ? intangibleAssets / totalAssets : 0;
  const goodwillRatio = totalAssets > 0 ? goodwill / totalAssets : 0;
  const hasIntangibleRisk = intangibleRatio > 0.2 || goodwillRatio > 0.1;

  // Tax efficiency
  const effectiveTaxRate = resultBeforeTax > 0 ? Math.abs(taxExpense) / resultBeforeTax : 0;
  const nominalTaxRate = 0.206; // Swedish corporate tax rate
  const taxEfficiencyGap = effectiveTaxRate - nominalTaxRate;

  // Operating leverage (degree of operating leverage)
  // DOL = Contribution margin / Operating income
  // Simplified: DOL ≈ 1 + (Fixed costs / Operating income)
  // We estimate based on variable costs ratio
  const contributionMargin = absNetSales * (1 - variableCostsRatio);
  const operatingLeverage = operatingResult > 0 ? contributionMargin / operatingResult : 0;

  const hasRisks = hasIntangibleRisk || (taxEfficiencyGap > 0.05 && resultBeforeTax > 0) || operatingLeverage > 3;

  if (!hasRisks && !isExpanded) return null;

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={18} className="text-amber-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Riskindikatorer
          </h3>
        </div>
        {isExpanded ? (
          <ChevronUp size={18} className="text-gray-400" />
        ) : (
          <ChevronDown size={18} className="text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          {/* Intangible assets risk */}
          {intangibleRatio > 0.05 && (
            <div className={`p-3 rounded-lg ${intangibleRatio > 0.2 ? "bg-amber-50" : "bg-gray-50"}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-medium text-gray-700">
                    Immateriella tillgångar
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {formatPercent(intangibleRatio * 100)} av tillgångarna
                  </p>
                </div>
                {goodwill > 0 && (
                  <span className="text-xs text-gray-500">
                    varav goodwill: {formatPercent(goodwillRatio * 100)}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {intangibleRatio > 0.3
                  ? "Hög andel — nedskrivningsrisk om verksamheten försämras."
                  : intangibleRatio > 0.2
                    ? "Betydande andel — håll koll på nedskrivningsbehov."
                    : "Måttlig andel immateriella tillgångar."}
              </p>
            </div>
          )}

          {/* Tax efficiency */}
          {resultBeforeTax > 0 && (
            <div className={`p-3 rounded-lg ${Math.abs(taxEfficiencyGap) > 0.05 ? "bg-amber-50" : "bg-gray-50"}`}>
              <div className="flex justify-between items-center">
                <p className="text-xs font-medium text-gray-700">
                  Skatteeffektivitet
                </p>
                <p className={`text-sm font-bold ${effectiveTaxRate <= nominalTaxRate ? "text-emerald-600" : "text-amber-600"}`}>
                  {formatPercent(effectiveTaxRate * 100)} effektiv skatt
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {effectiveTaxRate < nominalTaxRate - 0.03
                  ? `Lägre än nominell (${formatPercent(nominalTaxRate * 100)}) — bra skatteplanering.`
                  : effectiveTaxRate > nominalTaxRate + 0.03
                    ? `Högre än nominell — möjlig optimering?`
                    : `Nära nominell skattesats (${formatPercent(nominalTaxRate * 100)}).`}
              </p>
            </div>
          )}

          {/* Operating leverage */}
          {operatingLeverage > 0 && (
            <div className={`p-3 rounded-lg ${operatingLeverage > 3 ? "bg-amber-50" : "bg-gray-50"}`}>
              <div className="flex justify-between items-center">
                <p className="text-xs font-medium text-gray-700">
                  Operativ hävstång
                </p>
                <p className={`text-sm font-bold ${operatingLeverage <= 2 ? "text-emerald-600" : operatingLeverage <= 4 ? "text-amber-600" : "text-red-600"}`}>
                  {operatingLeverage.toFixed(1)}×
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {operatingLeverage > 4
                  ? "Hög — 1% omsättningsförändring ger ~4%+ resultatförändring."
                  : operatingLeverage > 2
                    ? "Måttlig — resultat är känsligt för omsättningsförändringar."
                    : "Låg — stabil resultatutveckling vid omsättningsförändringar."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// M&A Readiness Score
// ---------------------------------------------------------------------------

function MnAReadinessScore({
  netResult,
  prevNetResult,
  netSales,
  prevNetSales,
  totalAssets,
  solidityPercent,
  roe,
  hasAudit,
}: {
  netResult: number;
  prevNetResult: number | undefined;
  netSales: number;
  prevNetSales: number | undefined;
  totalAssets: number;
  solidityPercent: number;
  roe: number;
  hasAudit: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate M&A readiness factors
  const factors: { label: string; score: number; maxScore: number; reason: string }[] = [];

  // 1. Profitability (0-25 points)
  const profitScore =
    netResult > 0 && prevNetResult !== undefined && prevNetResult > 0
      ? 25
      : netResult > 0
        ? 15
        : netResult >= 0
          ? 5
          : 0;
  factors.push({
    label: "Lönsamhet",
    score: profitScore,
    maxScore: 25,
    reason: netResult > 0 ? "Lönsamt bolag" : netResult === 0 ? "Nollresultat" : "Förlust",
  });

  // 2. Growth (0-20 points)
  const revenueGrowth = prevNetSales && prevNetSales > 0
    ? ((Math.abs(netSales) - Math.abs(prevNetSales)) / Math.abs(prevNetSales)) * 100
    : 0;
  const growthScore =
    revenueGrowth > 20 ? 20 : revenueGrowth > 10 ? 15 : revenueGrowth > 0 ? 10 : revenueGrowth > -10 ? 5 : 0;
  factors.push({
    label: "Tillväxt",
    score: growthScore,
    maxScore: 20,
    reason: revenueGrowth > 0 ? `+${Math.round(revenueGrowth)}% omsättning` : `${Math.round(revenueGrowth)}% omsättning`,
  });

  // 3. Financial stability (0-20 points)
  const stabilityScore =
    solidityPercent >= 40 ? 20 : solidityPercent >= 30 ? 15 : solidityPercent >= 20 ? 10 : solidityPercent >= 10 ? 5 : 0;
  factors.push({
    label: "Finansiell stabilitet",
    score: stabilityScore,
    maxScore: 20,
    reason: `${Math.round(solidityPercent)}% soliditet`,
  });

  // 4. Return on equity (0-15 points)
  const roeScore = roe >= 0.2 ? 15 : roe >= 0.1 ? 10 : roe >= 0.05 ? 5 : roe > 0 ? 2 : 0;
  factors.push({
    label: "Avkastning (ROE)",
    score: roeScore,
    maxScore: 15,
    reason: `${(roe * 100).toFixed(1)}% ROE`,
  });

  // 5. Size (0-10 points) - larger companies are generally more attractive
  const sizeScore =
    totalAssets > 50000000 ? 10 : totalAssets > 10000000 ? 7 : totalAssets > 2000000 ? 4 : 2;
  factors.push({
    label: "Storlek",
    score: sizeScore,
    maxScore: 10,
    reason: formatCompact(totalAssets) + " i tillgångar",
  });

  // 6. Audit status (0-10 points)
  const auditScore = hasAudit ? 10 : 3;
  factors.push({
    label: "Revision",
    score: auditScore,
    maxScore: 10,
    reason: hasAudit ? "Har revisor" : "Saknar revisor",
  });

  const totalScore = factors.reduce((sum, f) => sum + f.score, 0);
  const maxTotalScore = factors.reduce((sum, f) => sum + f.maxScore, 0);
  const percentageScore = (totalScore / maxTotalScore) * 100;

  const rating =
    percentageScore >= 80
      ? { label: "Utmärkt", color: "text-emerald-600", bg: "bg-emerald-50" }
      : percentageScore >= 60
        ? { label: "God", color: "text-blue-600", bg: "bg-blue-50" }
        : percentageScore >= 40
          ? { label: "Acceptabel", color: "text-amber-600", bg: "bg-amber-50" }
          : { label: "Svag", color: "text-red-600", bg: "bg-red-50" };

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Target size={18} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Förvärvsberedskap
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 rounded-full ${rating.bg} ${rating.color}`}>
            {rating.label}
          </span>
          <span className={`text-lg font-bold ${rating.color}`}>
            {totalScore}/{maxTotalScore}
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            Bedömning av hur attraktivt bolaget är för potentiella köpare.
          </p>

          <div className="space-y-2">
            {factors.map((factor, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium">{factor.label}</span>
                    <span className="text-gray-500">{factor.reason}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${factor.score === factor.maxScore ? "bg-emerald-500" : factor.score >= factor.maxScore * 0.5 ? "bg-blue-500" : "bg-amber-500"}`}
                      style={{ width: `${(factor.score / factor.maxScore) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-600 w-12 text-right">
                  {factor.score}/{factor.maxScore}
                </span>
              </div>
            ))}
          </div>

          <div className={`p-3 rounded-lg ${rating.bg}`}>
            <p className={`text-sm ${rating.color}`}>
              {percentageScore >= 80
                ? "Bolaget har utmärkta förutsättningar att attrahera köpare."
                : percentageScore >= 60
                  ? "God förvärvsberedskap — attraktivt för strategiska köpare."
                  : percentageScore >= 40
                    ? "Acceptabel nivå — viss förbättringspotential innan försäljning."
                    : "Svag beredskap — betydande förbättringar behövs för att attrahera köpare."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Real Estate Metrics (Fastighetsbolag)
// ---------------------------------------------------------------------------

function RealEstateMetrics({
  buildings,
  totalAssets,
  longTermDebt,
  shortTermDebt,
  operatingResult,
  interestExpense,
  rentalIncome,
}: {
  buildings: number;
  totalAssets: number;
  longTermDebt: number;
  shortTermDebt: number;
  operatingResult: number;
  interestExpense: number;
  rentalIncome: number; // Approximated from net sales for property companies
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Skip if no buildings
  if (buildings <= 0) return null;

  const totalDebt = longTermDebt + shortTermDebt;

  // Loan-to-Value (LTV) = Total Debt / Property Value
  // For simplicity, we use buildings as property value
  const ltv = buildings > 0 ? (totalDebt / buildings) * 100 : 0;

  // Debt Service Coverage Ratio (DSCR) = NOI / Debt Service
  // Debt service approximated as interest expense (simplified)
  const noi = operatingResult; // Net Operating Income ≈ Operating Result for property companies
  const dscr = interestExpense > 0 ? noi / interestExpense : noi > 0 ? 999 : 0;

  // Capitalization Rate (Cap Rate) = NOI / Property Value
  const capRate = buildings > 0 ? (noi / buildings) * 100 : 0;

  // Yield on Cost = Rental Income / Total Assets
  const yieldOnCost = totalAssets > 0 ? (rentalIncome / totalAssets) * 100 : 0;

  // Property value as % of total assets
  const propertyRatio = totalAssets > 0 ? (buildings / totalAssets) * 100 : 0;

  const getLtvRating = (v: number) =>
    v <= 50 ? { label: "Låg", color: "text-emerald-600", bg: "bg-emerald-50" } :
    v <= 70 ? { label: "Normal", color: "text-blue-600", bg: "bg-blue-50" } :
    v <= 80 ? { label: "Hög", color: "text-amber-600", bg: "bg-amber-50" } :
    { label: "Mycket hög", color: "text-red-600", bg: "bg-red-50" };

  const getDscrRating = (v: number) =>
    v >= 1.5 ? { label: "Stark", color: "text-emerald-600", bg: "bg-emerald-50" } :
    v >= 1.25 ? { label: "God", color: "text-blue-600", bg: "bg-blue-50" } :
    v >= 1.0 ? { label: "Gräns", color: "text-amber-600", bg: "bg-amber-50" } :
    { label: "Svag", color: "text-red-600", bg: "bg-red-50" };

  const ltvRating = getLtvRating(ltv);
  const dscrRating = getDscrRating(dscr);

  return (
    <div className="card border-l-4 border-l-cyan-500">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Landmark size={18} className="text-cyan-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            Fastighetsanalys
          </h3>
          <span className="text-xs text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full">
            Branschspecifik
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 rounded-full ${ltvRating.bg} ${ltvRating.color}`}>
            LTV {ltv.toFixed(0)}%
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            Nyckeltal anpassade för fastighetsbolag. Dessa mått är mer relevanta än traditionella industrimodeller.
          </p>

          <div className="grid grid-cols-2 gap-3">
            {/* LTV */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Belåningsgrad (LTV)</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${ltvRating.bg} ${ltvRating.color}`}>
                  {ltvRating.label}
                </span>
              </div>
              <p className="text-lg font-bold text-gray-900">{ltv.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">Skuld / Fastighetsvärde</p>
            </div>

            {/* DSCR */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Räntetäckning (DSCR)</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${dscrRating.bg} ${dscrRating.color}`}>
                  {dscrRating.label}
                </span>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {dscr >= 100 ? ">99×" : `${dscr.toFixed(2)}×`}
              </p>
              <p className="text-xs text-gray-500 mt-1">Driftnetto / Räntekostnad</p>
            </div>

            {/* Cap Rate */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Direktavkastning</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{capRate.toFixed(2)}%</p>
              <p className="text-xs text-gray-500 mt-1">Driftnetto / Fastighetsvärde</p>
            </div>

            {/* Yield */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Avkastning på tillgångar</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{yieldOnCost.toFixed(2)}%</p>
              <p className="text-xs text-gray-500 mt-1">Hyresintäkter / Tillgångar</p>
            </div>
          </div>

          {/* Property ratio */}
          <div className="p-3 bg-cyan-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-cyan-800">Fastigheter av totala tillgångar</span>
              <span className="text-sm font-bold text-cyan-900">{propertyRatio.toFixed(1)}%</span>
            </div>
            <div className="mt-2 h-2 bg-cyan-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-cyan-500 rounded-full"
                style={{ width: `${Math.min(propertyRatio, 100)}%` }}
              />
            </div>
          </div>

          <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded">
            <strong>Om måtten:</strong> LTV under 50% anses konservativt, 50-70% normalt, över 70% högt.
            DSCR över 1.25× visar god förmåga att betala räntor. Direktavkastning (Cap Rate) varierar
            beroende på fastighetstyp och läge (3-8% är typiskt).
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Holding/Investment Company Metrics
// ---------------------------------------------------------------------------

function HoldingCompanyMetrics({
  financialAssets,
  totalAssets,
  equity,
  financialIncome,
  financialExpenses,
  netResult,
  longTermDebt,
  shortTermDebt,
}: {
  financialAssets: number;
  totalAssets: number;
  equity: number;
  financialIncome: number;
  financialExpenses: number;
  netResult: number;
  longTermDebt: number;
  shortTermDebt: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Skip if no significant financial assets
  if (financialAssets <= 0) return null;

  const totalDebt = longTermDebt + shortTermDebt;

  // Net Asset Value (NAV) - simplified as equity for unlisted holdings
  const nav = equity;

  // Financial assets as % of total
  const financialRatio = totalAssets > 0 ? (financialAssets / totalAssets) * 100 : 0;

  // Return on Financial Assets
  const netFinancialResult = financialIncome - Math.abs(financialExpenses);
  const rofa = financialAssets > 0 ? (netFinancialResult / financialAssets) * 100 : 0;

  // Dividend yield proxy (financial income / NAV)
  const yieldProxy = nav > 0 ? (financialIncome / nav) * 100 : 0;

  // Leverage ratio (debt / equity)
  const leverage = equity > 0 ? (totalDebt / equity) * 100 : 0;

  // NAV per investment quality
  const navQuality =
    netResult > 0 && leverage < 50 ? { label: "Stark", color: "text-emerald-600", bg: "bg-emerald-50" } :
    netResult >= 0 ? { label: "Stabil", color: "text-blue-600", bg: "bg-blue-50" } :
    { label: "Svag", color: "text-amber-600", bg: "bg-amber-50" };

  return (
    <div className="card border-l-4 border-l-purple-500">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Wallet size={18} className="text-purple-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            Holdingbolagsanalys
          </h3>
          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
            Branschspecifik
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 rounded-full ${navQuality.bg} ${navQuality.color}`}>
            {navQuality.label}
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            Nyckeltal anpassade för holding- och investmentbolag. Fokus på portföljvärde och avkastning.
          </p>

          <div className="grid grid-cols-2 gap-3">
            {/* NAV */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Substansvärde (NAV)</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${navQuality.bg} ${navQuality.color}`}>
                  {navQuality.label}
                </span>
              </div>
              <p className="text-lg font-bold text-gray-900">{formatCompact(nav)}</p>
              <p className="text-xs text-gray-500 mt-1">Bokfört eget kapital</p>
            </div>

            {/* Financial Assets */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Finansiella tillgångar</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{formatCompact(financialAssets)}</p>
              <p className="text-xs text-gray-500 mt-1">{financialRatio.toFixed(1)}% av tillgångar</p>
            </div>

            {/* Return on Financial Assets */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Avkastning fin. tillg.</span>
              </div>
              <p className={`text-lg font-bold ${rofa >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {rofa.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">Fin. netto / Fin. tillgångar</p>
            </div>

            {/* Yield Proxy */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Utdelningsavkastning</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{yieldProxy.toFixed(2)}%</p>
              <p className="text-xs text-gray-500 mt-1">Fin. intäkter / NAV</p>
            </div>
          </div>

          {/* Leverage bar */}
          <div className="p-3 bg-purple-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-purple-800">Skuldsättningsgrad</span>
              <span className="text-sm font-bold text-purple-900">{leverage.toFixed(1)}%</span>
            </div>
            <div className="mt-2 h-2 bg-purple-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${leverage > 100 ? "bg-red-500" : leverage > 50 ? "bg-amber-500" : "bg-purple-500"}`}
                style={{ width: `${Math.min(leverage, 100)}%` }}
              />
            </div>
            <p className="text-xs text-purple-700 mt-1">
              {leverage < 30 ? "Konservativ belåning" : leverage < 50 ? "Måttlig belåning" : leverage < 100 ? "Hög belåning" : "Mycket hög belåning"}
            </p>
          </div>

          <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded">
            <strong>Om måtten:</strong> Substansvärde (NAV) visar bokfört värde — verkligt värde kan avvika
            betydligt för onoterade innehav. Avkastning på finansiella tillgångar visar portföljens
            direkta bidrag. Låg skuldsättning (&lt;50%) ger flexibilitet vid nedgångar.
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Knowledge/Service Company Metrics (Kunskapsföretag)
// ---------------------------------------------------------------------------

function KnowledgeCompanyMetrics({
  netSales,
  personnelCosts,
  operatingResult,
  averageEmployees,
  netResult,
  intangibleAssets,
  totalAssets,
}: {
  netSales: number;
  personnelCosts: number;
  operatingResult: number;
  averageEmployees: number | undefined;
  netResult: number;
  intangibleAssets: number;
  totalAssets: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const absNetSales = Math.abs(netSales);
  const absPersonnelCosts = Math.abs(personnelCosts);

  // Skip if no personnel costs (not a knowledge company)
  if (absPersonnelCosts <= 0) return null;

  // Revenue per employee (if available)
  const revenuePerEmployee = averageEmployees && averageEmployees > 0
    ? absNetSales / averageEmployees
    : null;

  // Profit per employee
  const profitPerEmployee = averageEmployees && averageEmployees > 0
    ? netResult / averageEmployees
    : null;

  // Personnel cost ratio
  const personnelRatio = absNetSales > 0 ? (absPersonnelCosts / absNetSales) * 100 : 0;

  // Value added per employee = (Revenue - External costs) / Employees
  // Simplified as (Operating Result + Personnel) / Employees
  const valueAddedPerEmployee = averageEmployees && averageEmployees > 0
    ? (operatingResult + absPersonnelCosts) / averageEmployees
    : null;

  // Knowledge intensity = Intangible assets / Total assets
  const knowledgeIntensity = totalAssets > 0 ? (intangibleAssets / totalAssets) * 100 : 0;

  const getEfficiencyRating = () => {
    if (!revenuePerEmployee) return { label: "Okänd", color: "text-gray-600", bg: "bg-gray-50" };
    if (revenuePerEmployee >= 2000000) return { label: "Utmärkt", color: "text-emerald-600", bg: "bg-emerald-50" };
    if (revenuePerEmployee >= 1500000) return { label: "God", color: "text-blue-600", bg: "bg-blue-50" };
    if (revenuePerEmployee >= 1000000) return { label: "Normal", color: "text-amber-600", bg: "bg-amber-50" };
    return { label: "Låg", color: "text-red-600", bg: "bg-red-50" };
  };

  const efficiencyRating = getEfficiencyRating();

  return (
    <div className="card border-l-4 border-l-indigo-500">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Calculator size={18} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            Kunskapsföretagsanalys
          </h3>
          <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
            Branschspecifik
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 rounded-full ${efficiencyRating.bg} ${efficiencyRating.color}`}>
            {efficiencyRating.label}
          </span>
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            Nyckeltal för kunskaps- och tjänsteföretag där humankapital är den viktigaste tillgången.
          </p>

          <div className="grid grid-cols-2 gap-3">
            {/* Revenue per employee */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Omsättning/anställd</span>
                {revenuePerEmployee && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${efficiencyRating.bg} ${efficiencyRating.color}`}>
                    {efficiencyRating.label}
                  </span>
                )}
              </div>
              <p className="text-lg font-bold text-gray-900">
                {revenuePerEmployee ? formatCompact(revenuePerEmployee) : "—"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {averageEmployees ? `${averageEmployees} anställda` : "Antal anställda saknas"}
              </p>
            </div>

            {/* Profit per employee */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Vinst/anställd</span>
              </div>
              <p className={`text-lg font-bold ${profitPerEmployee && profitPerEmployee > 0 ? "text-emerald-600" : profitPerEmployee && profitPerEmployee < 0 ? "text-red-600" : "text-gray-900"}`}>
                {profitPerEmployee ? formatCompact(profitPerEmployee) : "—"}
              </p>
              <p className="text-xs text-gray-500 mt-1">Nettoresultat/anställd</p>
            </div>

            {/* Personnel cost ratio */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Personalkostnadsandel</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{personnelRatio.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">
                {personnelRatio > 60 ? "Hög (typiskt konsult)" : personnelRatio > 40 ? "Normal" : "Låg"}
              </p>
            </div>

            {/* Value added per employee */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Förädlingsvärde/anst.</span>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {valueAddedPerEmployee ? formatCompact(valueAddedPerEmployee) : "—"}
              </p>
              <p className="text-xs text-gray-500 mt-1">(Rörelseresultat + Personal) / Anst.</p>
            </div>
          </div>

          {/* Personnel ratio bar */}
          <div className="p-3 bg-indigo-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-indigo-800">Personalkostnad av omsättning</span>
              <span className="text-sm font-bold text-indigo-900">{personnelRatio.toFixed(1)}%</span>
            </div>
            <div className="mt-2 h-2 bg-indigo-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 rounded-full"
                style={{ width: `${Math.min(personnelRatio, 100)}%` }}
              />
            </div>
          </div>

          {/* Knowledge intensity */}
          {knowledgeIntensity > 0 && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">Immateriella tillgångar</span>
                <span className="text-sm font-bold text-gray-900">{knowledgeIntensity.toFixed(1)}%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Andel av totala tillgångar (patent, licenser, goodwill etc.)
              </p>
            </div>
          )}

          <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded">
            <strong>Om måtten:</strong> Omsättning/anställd över 1,5 MSEK är bra för svenska konsultbolag.
            Personalkostnad på 50-70% är typiskt för kunskapsföretag. Förädlingsvärde/anställd visar
            hur mycket värde varje medarbetare skapar.
          </div>
        </div>
      )}
    </div>
  );
}

function MethodologySection() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <HelpCircle size={18} className="text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-600">
            Så beräknas analysen
          </h3>
        </div>
        {isExpanded ? (
          <ChevronUp size={18} className="text-gray-400" />
        ) : (
          <ChevronDown size={18} className="text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4 text-sm text-gray-600">
          <div>
            <p className="font-medium text-gray-700 mb-1">Hälsopoäng</p>
            <p>
              Hälsopoängen är ett viktat genomsnitt av fem finansiella
              dimensioner. Varje dimension bedöms som{" "}
              <span className="text-emerald-600 font-medium">grön (5p)</span>,{" "}
              <span className="text-amber-600 font-medium">gul (3p)</span>{" "}
              eller{" "}
              <span className="text-red-600 font-medium">röd (1p)</span>{" "}
              baserat på tröskelvärden. Genomsnittet ger en poäng mellan 1.0
              och 5.0.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-700 text-xs mb-1">
                Soliditet
              </p>
              <p className="text-xs text-gray-500">
                Justerat eget kapital / Totala tillgångar. Visar hur stor
                andel av tillgångarna som finansieras med eget kapital.
                Gränsvärden: ≥30 % grön, 20–30 % gul, &lt;20 % röd.
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-700 text-xs mb-1">
                Lönsamhet
              </p>
              <p className="text-xs text-gray-500">
                Baseras på årets resultat (nettoresultat). Positivt resultat
                ger grönt, nollresultat gult, negativt rött.
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-700 text-xs mb-1">
                Likviditet
              </p>
              <p className="text-xs text-gray-500">
                Omsättningstillgångar / Kortfristiga skulder
                (balanslikviditet). Visar betalningsförmåga på kort sikt.
                Gränsvärden: ≥1,5 grön, 1,0–1,5 gul, &lt;1,0 röd.
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-700 text-xs mb-1">
                Skuldsättning
              </p>
              <p className="text-xs text-gray-500">
                (Långfristiga + Kortfristiga skulder) / Totala tillgångar.
                Gränsvärden: &lt;50 % grön, 50–70 % gul, &gt;70 % röd.
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg md:col-span-2">
              <p className="font-medium text-gray-700 text-xs mb-1">
                Kapitalskydd
              </p>
              <p className="text-xs text-gray-500">
                Jämför eget kapital med aktiekapitalet. Om eget kapital
                understiger halva aktiekapitalet ska styrelsen enligt ABL 25
                kap 13§ upprätta kontrollbalansräkning.
              </p>
            </div>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-1">
              Lönsamhetsmarginaler
            </p>
            <p>
              Rörelsemarginal = Rörelseresultat / Nettoomsättning.
              Nettomarginal = Årets resultat / Nettoomsättning. Marginalerna
              visar hur effektivt företaget omvandlar omsättning till vinst.
              ≥10 % anses starkt, 3–10 % acceptabelt, under 3 % svagt.
            </p>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-1">Effektivitetstal</p>
            <p>
              Räntabilitet på eget kapital (ROE) = Nettoresultat / Eget
              kapital. Räntabilitet på totalt kapital (ROTC) = Rörelseresultat
              / Totala tillgångar. Kapitalomsättningshastighet =
              Nettoomsättning / Totala tillgångar. Rörelsekapital =
              Omsättningstillgångar − Kortfristiga skulder. Snabblikviditet =
              (Omsättningstillgångar − Varulager) / Kortfristiga skulder.
            </p>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-1">
              Säkerhetsmarginal &amp; break-even
            </p>
            <p>
              Säkerhetsmarginalen visar hur mycket omsättningen kan sjunka
              innan rörelseresultatet blir noll: Rörelseresultat /
              Nettoomsättning × 100. Break-even-omsättning = Nettoomsättning
              − Rörelseresultat. ≥20 % anses starkt, 10–20 % acceptabelt,
              under 10 % svagt.
            </p>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-1">
              Värderingsindikation
            </p>
            <p>
              Tre förenklade metoder: <strong>Substansvärde</strong> =
              Justerat eget kapital (bokfört EK + 79,4 % av obeskattade
              reserver). <strong>Avkastningsvärde</strong> = Normaliserat
              resultat × 3–7 (branschberoende P/E-multipel).{" "}
              <strong>EV/EBITDA</strong> = Rörelseresultat + avskrivningar ×
              4–8 (företagsvärde baserat på resultat före avskrivningar).
            </p>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-1">
              Bolagsprofil &amp; primärmetod
            </p>
            <p>
              Systemet analyserar balansräkningens sammansättning och
              resultaträkningens kostnadsstruktur för att identifiera
              företagets profil och rekommendera den mest relevanta
              värderingsmetoden:
            </p>
            <div className="grid md:grid-cols-2 gap-2 mt-2">
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700">
                  Holdingbolag → Substansvärde
                </p>
                <p className="text-xs text-gray-500">
                  Finansiella tillgångar &gt;40 % och mycket låg omsättning.
                  Värdet beror på underliggande dotterbolags värde. Bokfört
                  värde (anskaffningsvärde) kan avvika kraftigt från verkligt.
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700">
                  Investmentbolag → Substansvärde
                </p>
                <p className="text-xs text-gray-500">
                  Finansiella tillgångar dominerar med viss utdelnings-/
                  ränteintäkt. Till skillnad från holdingbolag har innehaven
                  ofta observerbara marknadsvärden.
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700">
                  Fastighetsbolag → Substansvärde
                </p>
                <p className="text-xs text-gray-500">
                  Byggnader utgör &gt;30 % av tillgångarna. Under K2 bokförs
                  fastigheter till anskaffningsvärde minus avskrivningar.
                  Systemet visar marknadsjusterat substansvärde (1,5–3×).
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700">
                  Kunskapsbolag → Avkastningsvärde
                </p>
                <p className="text-xs text-gray-500">
                  Mycket hög personalkostnadsandel (&gt;50 %), minimala
                  materiella tillgångar. Värdet ligger i kompetens och
                  kundrelationer — starkt nyckelpersonsberoende.
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700">
                  Tjänsteföretag → Avkastningsvärde
                </p>
                <p className="text-xs text-gray-500">
                  Låg andel materiella tillgångar, hög personalkostnadsandel.
                  Värdet ligger i intjäningsförmågan, inte i tillgångarna.
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700">
                  Handelsföretag → Avkastningsvärde
                </p>
                <p className="text-xs text-gray-500">
                  Hög varulagersandel och varuinköp. Substansvärdet används som
                  golvvärde.
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700">
                  Tillverkningsföretag → EV/EBITDA
                </p>
                <p className="text-xs text-gray-500">
                  Kapitalintensivt med betydande avskrivningar. EV/EBITDA
                  neutraliserar avskrivningspåverkan.
                </p>
              </div>
            </div>
            <p className="mt-2">
              Verklig företagsvärdering kräver djupare analys av marknads-
              förutsättningar, kassaflöden och framtidsutsikter.
            </p>
          </div>

          <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
            All data hämtas från företagets bokslut (SIE-fil). Analysen är
            en automatisk indikation och ersätter inte professionell
            rådgivning.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FinancialHealth({
  report,
  company,
  isLoading,
  availableReports,
  selectedReportId,
  onSelectReport,
}: FinancialHealthProps) {
  // Hooks must be called unconditionally (before any early returns)
  // Profile override state - allows user to change the detected profile
  const [overrideProfile, setOverrideProfile] = useState<CompanyProfile | null>(
    company.financialHealthProfile as CompanyProfile | null
  );
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Sync state when company changes (e.g., navigation between companies)
  useEffect(() => {
    setOverrideProfile(company.financialHealthProfile as CompanyProfile | null);
  }, [company.id, company.financialHealthProfile]);

  // Save profile preference to database
  const handleProfileChange = async (profile: CompanyProfile | null) => {
    setOverrideProfile(profile);
    setIsSavingProfile(true);
    try {
      await companiesApi.updateFinancialHealthProfile(company.id, profile);
      toast.success(profile ? `Bolagsprofil sparad: ${PROFILES[profile].label}` : "Automatisk profilering aktiverad");
    } catch {
      toast.error("Kunde inte spara bolagsprofil");
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Spinner size="lg" />
        <p className="text-gray-500 mt-3">Laddar finansiell data...</p>
      </div>
    );
  }

  // No data state
  if (!report || !report.parsedSieData) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HeartPulse size={32} className="text-rose-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Ingen finansiell data tillgänglig
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Skapa och slutför en årsredovisning för att se {company.name}s
            ekonomiska hälsa, nyckeltal och trender.
          </p>
        </div>
      </div>
    );
  }

  const data: ParsedSieData = report.parsedSieData;
  const kf = data.keyFigures;
  const bs = data.balanceSheet;
  const is = data.incomeStatement;
  const prevIS = data.previousYearIncomeStatement;
  const prevKF = data.previousYearKeyFigures;
  const fiscalYear = `${report.fiscalYearStart} — ${report.fiscalYearEnd}`;

  // Detect company profile for analytics filtering
  const profileDetection = detectCompanyProfile({
    buildings: bs.assets.fixedAssets.buildings || 0,
    tangibleAssets: bs.assets.fixedAssets.tangible || 0,
    financialAssets: bs.assets.fixedAssets.financial || 0,
    totalAssets: bs.assets.totalAssets,
    inventory: bs.assets.currentAssets.inventory,
    netSales: kf.netSales,
    personnelCosts: is.expenses.personnelCosts,
    goodsAndMaterials: is.expenses.goodsAndMaterials,
    previousBuildings: data.previousYearBalanceSheet?.assets.fixedAssets.buildings,
  });

  // Use override if set, otherwise use detected profile
  const activeProfile = overrideProfile ? PROFILES[overrideProfile] : profileDetection.profile;
  const activeProfileKey: CompanyProfile = activeProfile.id;
  const isProfileOverridden = overrideProfile !== null;

  // Health dimensions
  const dimensions = computeHealthDimensions(kf, bs, is);
  const score = computeHealthScore(dimensions);
  const insights = computeInsights(kf, bs, is, report.userInput);

  // KPI trends
  const salesTrend = trendPercent(kf.netSales, prevKF?.netSales);
  const resultTrend = trendPercent(kf.netResult, prevKF?.netResult);
  const solidityTrend =
    prevKF != null
      ? kf.solidityPercent - prevKF.solidityPercent
      : undefined;

  // Result development bar chart data
  const resultDevData: Record<string, unknown>[] = [
    {
      label: "Nettoomsättning",
      current: Math.abs(kf.netSales),
      previous: Math.abs(prevKF?.netSales || 0),
    },
    {
      label: "Rörelsekostnader",
      current: Math.abs(is.expenses.total),
      previous: Math.abs(prevIS?.expenses.total || 0),
    },
    {
      label: "Rörelseresultat",
      current: is.operatingResult,
      previous: prevIS?.operatingResult || 0,
    },
    {
      label: "Årets resultat",
      current: is.netResult,
      previous: prevIS?.netResult || 0,
    },
  ];

  // Asset composition donut
  const assetData = [
    {
      name: "Immateriella tillgångar",
      value: bs.assets.fixedAssets.intangible,
      color: "#8b5cf6",
    },
    {
      name: "Materiella tillgångar",
      value: bs.assets.fixedAssets.tangible,
      color: "#0ea5e9",
    },
    {
      name: "Finansiella tillgångar",
      value: bs.assets.fixedAssets.financial,
      color: "#06b6d4",
    },
    {
      name: "Varulager",
      value: bs.assets.currentAssets.inventory,
      color: "#f59e0b",
    },
    {
      name: "Fordringar",
      value: bs.assets.currentAssets.receivables,
      color: "#10b981",
    },
    {
      name: "Kassa & bank",
      value: bs.assets.currentAssets.cashAndBank,
      color: "#22c55e",
    },
  ].filter((d) => d.value > 0);

  // Financing composition donut
  const financingData = [
    {
      name: "Eget kapital",
      value: Math.max(0, bs.equityAndLiabilities.equity.total),
      color: "#10b981",
    },
    {
      name: "Obeskattade reserver",
      value: bs.equityAndLiabilities.untaxedReserves,
      color: "#22c55e",
    },
    {
      name: "Avsättningar",
      value: bs.equityAndLiabilities.provisions,
      color: "#06b6d4",
    },
    {
      name: "Långfristiga skulder",
      value: bs.equityAndLiabilities.longTermLiabilities,
      color: "#f59e0b",
    },
    {
      name: "Kortfristiga skulder",
      value: bs.equityAndLiabilities.shortTermLiabilities,
      color: "#f43f5e",
    },
  ].filter((d) => d.value > 0);

  // Cost breakdown bar chart data
  const costData: Record<string, unknown>[] = [
    {
      label: "Personal",
      value: Math.abs(is.expenses.personnelCosts),
    },
    {
      label: "Övriga externa",
      value: Math.abs(is.expenses.otherExternalCosts),
    },
    {
      label: "Varor & material",
      value: Math.abs(is.expenses.goodsAndMaterials),
    },
    {
      label: "Avskrivningar",
      value: Math.abs(is.expenses.depreciationAmortization),
    },
    {
      label: "Finansiella",
      value: Math.abs(is.financialItems.financialExpenses),
    },
  ]
    .filter((d) => (d.value as number) > 0)
    .sort((a, b) => (b.value as number) - (a.value as number));

  const yFmt = (v: number) => formatCompact(v, false);
  const centerLabelFmt = (v: number) => formatCompact(v);
  const hasPreviousYear = prevIS != null || prevKF != null;

  return (
    <div className="space-y-6">
      {/* Section 1: Health Overview */}
      <HealthOverview
        dimensions={dimensions}
        score={score}
        fiscalYear={fiscalYear}
        availableReports={availableReports}
        selectedReportId={selectedReportId}
        onSelectReport={onSelectReport}
      />

      {/* Section 2: KPI StatCards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Nettoomsättning"
          value={kf.netSales}
          icon={TrendingUp}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          trend={salesTrend}
          trendLabel="vs föregående år"
          formatValue={(v) => formatCurrency(v as number)}
          formatValueCompact={(v) => formatCompact(v as number)}
        />
        <StatCard
          label="Årets resultat"
          value={kf.netResult}
          icon={kf.netResult >= 0 ? TrendingUp : TrendingDown}
          iconBg={kf.netResult >= 0 ? "bg-emerald-100" : "bg-red-100"}
          iconColor={kf.netResult >= 0 ? "text-emerald-600" : "text-red-600"}
          trend={resultTrend}
          trendLabel="vs föregående år"
          formatValue={(v) => formatCurrency(v as number)}
          formatValueCompact={(v) => formatCompact(v as number)}
        />
        <StatCard
          label="Soliditet"
          value={kf.solidityPercent}
          icon={Scale}
          iconBg="bg-violet-100"
          iconColor="text-violet-600"
          trend={solidityTrend}
          trendLabel="procentenheter"
          formatValue={(v) => formatPercent(v as number)}
        />
        <StatCard
          label="Fritt eget kapital"
          value={kf.freeEquity}
          icon={Landmark}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          formatValue={(v) => formatCurrency(v as number)}
          formatValueCompact={(v) => formatCompact(v as number)}
        />
      </div>

      {/* Section 2b: Profitability Margins + Cash Position */}
      <div className="grid md:grid-cols-2 gap-4">
        <ProfitabilityMargins
          operatingResult={is.operatingResult}
          netResult={is.netResult}
          netSales={kf.netSales}
        />
        <CashPositionIndicator
          currentCash={bs.assets.currentAssets.cashAndBank}
          previousCash={data.previousYearBalanceSheet?.assets.currentAssets.cashAndBank}
        />
      </div>

      {/* Section 2c: Efficiency Ratios */}
      <EfficiencyRatios
        netResult={is.netResult}
        operatingResult={is.operatingResult}
        equity={bs.equityAndLiabilities.equity.total}
        totalAssets={bs.assets.totalAssets}
        netSales={kf.netSales}
        currentAssets={bs.assets.currentAssets.total}
        shortTermLiabilities={bs.equityAndLiabilities.shortTermLiabilities}
        inventory={bs.assets.currentAssets.inventory}
      />

      {/* Section 3: Result Development */}
      {hasPreviousYear && (
        <ChartCard
          title="Resultatutveckling"
          description="Jämförelse med föregående räkenskapsår"
        >
          <BarChartWidget
            data={resultDevData}
            xKey="label"
            series={[
              { dataKey: "current", label: "Innevarande år", color: "#4f46e5" },
              {
                dataKey: "previous",
                label: "Föregående år",
                color: "#d1d5db",
              },
            ]}
            showLegend
            yFormatter={yFmt}
            height={300}
            radius={3}
          />
        </ChartCard>
      )}

      {/* Section 4: Balance Structure — two donuts side by side */}
      <div className="grid md:grid-cols-2 gap-4">
        {assetData.length > 0 && (
          <ChartCard title="Tillgångsfördelning" minHeight={280}>
            <DonutChartWidget
              data={assetData}
              formatValue={(v) => formatCompact(v)}
              formatCenterLabel={centerLabelFmt}
              height={260}
              centerLabel="total"
            />
          </ChartCard>
        )}
        {financingData.length > 0 && (
          <ChartCard title="Finansieringsstruktur" minHeight={280}>
            <DonutChartWidget
              data={financingData}
              formatValue={(v) => formatCompact(v)}
              formatCenterLabel={centerLabelFmt}
              height={260}
              centerLabel="total"
            />
          </ChartCard>
        )}
      </div>

      {/* Section 5: Cost Breakdown */}
      {costData.length > 0 && (
        <ChartCard title="Kostnadsfördelning">
          <BarChartWidget
            data={costData}
            xKey="label"
            series={[
              { dataKey: "value", label: "Belopp", color: "#6366f1" },
            ]}
            layout="horizontal"
            yFormatter={yFmt}
            height={Math.max(200, costData.length * 50)}
            radius={3}
          />
        </ChartCard>
      )}

      {/* Section 5b: Break-Even Indicator */}
      <BreakEvenIndicator
        netSales={kf.netSales}
        operatingResult={is.operatingResult}
      />

      {/* Section 6: Valuation Estimate */}
      <ValuationEstimate
        adjustedEquity={kf.adjustedEquity}
        equity={bs.equityAndLiabilities.equity.total}
        shareCapital={bs.equityAndLiabilities.equity.shareCapital}
        netResult={is.netResult}
        prevNetResult={prevKF?.netResult}
        prevPrevNetResult={undefined} // Would need third year data
        operatingResult={is.operatingResult}
        depreciationAmortization={is.expenses.depreciationAmortization}
        totalAssets={bs.assets.totalAssets}
        buildings={bs.assets.fixedAssets.buildings}
        netSales={kf.netSales}
        prevNetSales={prevKF?.netSales}
        activeProfile={activeProfile}
        profileDetection={profileDetection}
        isProfileOverridden={isProfileOverridden}
        onProfileChange={handleProfileChange}
        isSavingProfile={isSavingProfile}
      />

      {/* Section 7: Advanced Analytics */}
      <div className="space-y-4">
        {/* === PROFILE-SPECIFIC METRICS (shown first when relevant) === */}

        {/* Real Estate Metrics - only for property companies */}
        {isModelSuitableForProfile("realEstateMetrics", activeProfileKey) && (
          <RealEstateMetrics
            buildings={bs.assets.fixedAssets.buildings || 0}
            totalAssets={bs.assets.totalAssets}
            longTermDebt={bs.equityAndLiabilities.longTermLiabilities}
            shortTermDebt={bs.equityAndLiabilities.shortTermLiabilities}
            operatingResult={is.operatingResult}
            interestExpense={is.financialItems.financialExpenses || 0}
            rentalIncome={Math.abs(kf.netSales)}
          />
        )}

        {/* Holding/Investment Company Metrics */}
        {isModelSuitableForProfile("holdingMetrics", activeProfileKey) && (
          <HoldingCompanyMetrics
            financialAssets={bs.assets.fixedAssets.financial}
            totalAssets={bs.assets.totalAssets}
            equity={bs.equityAndLiabilities.equity.total}
            financialIncome={is.financialItems.financialIncome || 0}
            financialExpenses={is.financialItems.financialExpenses || 0}
            netResult={is.netResult}
            longTermDebt={bs.equityAndLiabilities.longTermLiabilities}
            shortTermDebt={bs.equityAndLiabilities.shortTermLiabilities}
          />
        )}

        {/* Knowledge/Service Company Metrics */}
        {isModelSuitableForProfile("knowledgeCompanyMetrics", activeProfileKey) && (
          <KnowledgeCompanyMetrics
            netSales={kf.netSales}
            personnelCosts={is.expenses.personnelCosts}
            operatingResult={is.operatingResult}
            averageEmployees={report.userInput?.averageEmployees}
            netResult={is.netResult}
            intangibleAssets={bs.assets.fixedAssets.intangible}
            totalAssets={bs.assets.totalAssets}
          />
        )}

        {/* === GENERAL MODELS (filtered by profile suitability) === */}

        {/* Altman Z-Score - NOT suitable for property, holding, investment */}
        {isModelSuitableForProfile("altmanZScore", activeProfileKey) && (
          <AltmanZScoreCard
            workingCapital={bs.assets.currentAssets.total - bs.equityAndLiabilities.shortTermLiabilities}
            totalAssets={bs.assets.totalAssets}
            retainedEarnings={bs.equityAndLiabilities.equity.retainedEarnings || 0}
            ebit={is.operatingResult}
            equity={bs.equityAndLiabilities.equity.total}
            totalDebt={bs.equityAndLiabilities.longTermLiabilities + bs.equityAndLiabilities.shortTermLiabilities}
            sales={Math.abs(kf.netSales)}
          />
        )}

        {/* DuPont Analysis - NOT suitable for pure holding */}
        {isModelSuitableForProfile("dupontAnalysis", activeProfileKey) && (
          <DuPontAnalysis
            netResult={is.netResult}
            netSales={Math.abs(kf.netSales)}
            totalAssets={bs.assets.totalAssets}
            equity={bs.equityAndLiabilities.equity.total}
          />
        )}

        {/* Working Capital Cycle - NOT suitable for property, holding, investment */}
        {isModelSuitableForProfile("workingCapitalCycle", activeProfileKey) && (
          <WorkingCapitalCycle
            accountsReceivable={bs.assets.currentAssets.receivables || 0}
            accountsPayable={bs.equityAndLiabilities.shortTermLiabilities * 0.4}
            inventory={bs.assets.currentAssets.inventory}
            netSales={Math.abs(kf.netSales)}
            costOfGoodsSold={Math.abs(is.expenses.goodsAndMaterials)}
          />
        )}

        {/* Debt Analysis - relevant for all */}
        {isModelSuitableForProfile("debtAnalysis", activeProfileKey) && (
          <DebtAnalysis
            operatingResult={is.operatingResult}
            interestExpense={is.financialItems.financialExpenses || 0}
            totalDebt={bs.equityAndLiabilities.longTermLiabilities + bs.equityAndLiabilities.shortTermLiabilities}
            equity={bs.equityAndLiabilities.equity.total}
            ebitda={is.operatingResult + Math.abs(is.expenses.depreciationAmortization)}
          />
        )}

        {/* Sustainable Growth Rate - NOT suitable for pure holding */}
        {isModelSuitableForProfile("sustainableGrowth", activeProfileKey) && (
          <SustainableGrowthRate
            netResult={is.netResult}
            equity={bs.equityAndLiabilities.equity.total}
            proposedDividend={report.userInput?.proposedDividend || 0}
          />
        )}

        {/* Liquidation Value - relevant for all (especially distressed) */}
        {isModelSuitableForProfile("liquidationValue", activeProfileKey) && (
          <LiquidationValueAnalysis
            cashAndBank={bs.assets.currentAssets.cashAndBank}
            accountsReceivable={bs.assets.currentAssets.receivables || 0}
            inventory={bs.assets.currentAssets.inventory}
            otherCurrentAssets={bs.assets.currentAssets.prepaidExpenses || 0}
            buildings={bs.assets.fixedAssets.buildings || 0}
            machineryAndEquipment={(bs.assets.fixedAssets.tangible || 0) - (bs.assets.fixedAssets.buildings || 0)}
            financialAssets={bs.assets.fixedAssets.financial}
            intangibleAssets={bs.assets.fixedAssets.intangible}
            equity={bs.equityAndLiabilities.equity.total}
            isInDistress={bs.equityAndLiabilities.equity.total < bs.equityAndLiabilities.equity.shareCapital / 2}
          />
        )}

        {/* Risk Indicators - relevant for all */}
        {isModelSuitableForProfile("riskIndicators", activeProfileKey) && (
          <RiskIndicators
            intangibleAssets={bs.assets.fixedAssets.intangible}
            totalAssets={bs.assets.totalAssets}
            goodwill={0}
            taxExpense={is.taxOnResult || 0}
            resultBeforeTax={is.resultAfterFinancialItems}
            operatingResult={is.operatingResult}
            netSales={Math.abs(kf.netSales)}
            variableCostsRatio={Math.abs(is.expenses.goodsAndMaterials) / Math.max(Math.abs(kf.netSales), 1)}
          />
        )}

        {/* M&A Readiness - relevant for all */}
        {isModelSuitableForProfile("mnaReadiness", activeProfileKey) && (
          <MnAReadinessScore
            netResult={is.netResult}
            prevNetResult={prevKF?.netResult}
            netSales={Math.abs(kf.netSales)}
            prevNetSales={prevKF?.netSales ? Math.abs(prevKF.netSales) : undefined}
            totalAssets={bs.assets.totalAssets}
            solidityPercent={kf.solidityPercent}
            roe={bs.equityAndLiabilities.equity.total > 0 ? is.netResult / bs.equityAndLiabilities.equity.total : 0}
            hasAudit={company.boardMembers?.some(m => m.role.toLowerCase().includes('revisor')) ?? false}
          />
        )}
      </div>

      {/* Section 8: Insights & Warnings */}
      <InsightsSection insights={insights} />

      {/* Section 9: Dividend Capacity */}
      <DividendCapacity
        equity={bs.equityAndLiabilities.equity}
        userInput={report.userInput}
      />

      {/* Section 10: Methodology */}
      <MethodologySection />
    </div>
  );
}
